import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { API, apiGet } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Plus, Trash2, Info, Loader2, Upload, BarChart3, Download, Database, RefreshCw, CheckCircle2, AlertTriangle, Boxes, PartyPopper } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import usePageTitle from "../hooks/usePageTitle";
import { PageHeader } from "../components/dc";

// Left-nav sections for the Settings sidebar (reference design). Each maps to an
// existing self-contained card component (or the inline mappings panel).
const SETTINGS_GROUPS = [
  { group: "Pricing & analytics", items: [
    ["standard-suppliers", "Standard Suppliers"],
    ["mappings", "Supplier Mappings"],
    ["threshold", "Overcharge Threshold"],
    ["fx", "Currency & FX"],
    ["indexes", "Market Indexes"],
    ["exclusion", "Transport Exclusion"],
    ["sku", "SKU List"],
    ["sku-aliases", "SKU Aliases"],
  ] },
  { group: "Reminders & alerts", items: [
    ["reminder", "Invoice Reminder"],
    ["price-expiry", "Stale Price Reminder"],
    ["price-drift", "Price Drift Alert"],
    ["reduction-alerts", "Reduction Alerts"],
    ["manager-digest", "Manager Digest"],
    ["board-pack", "Board Pack"],
    ["supplier-confirmation", "Supplier Confirmation"],
  ] },
  { group: "Price modelling", items: [
    ["supplier-map", "Dispatch Supplier Map"],
    ["forecast", "Install Forecast"],
    ["product-map", "Product Type Map"],
    ["variant-map", "Variant Map"],
    ["review-preview", "Review Queue Preview"],
  ] },
  { group: "Budget modeller", items: [
    ["platform-bom", "Platform BOM"],
    ["budget-drivers", "Inflation Drivers"],
    ["budget-volumes", "Budget Volumes"],
  ] },
  { group: "Display & data", items: [
    ["milestone", "TV Milestone"],
    ["engineer-regions", "Engineer Regions"],
    ["feedback-sync", "Supplier Feedback"],
    ["ss-supplier-map", "Supplier Mapping"],
    ["recompute", "Recompute"],
    ["export", "Export"],
  ] },
  { group: "System", items: [
    ["system-status", "System Status"],
  ] },
];

const Settings = () => {
  const [section, setSection] = useState("standard-suppliers");
  const [mappings, setMappings] = useState(null);
  const [unmappedSuppliers, setUnmappedSuppliers] = useState([]);
  const [manualRawName, setManualRawName] = useState("");
  const [selectedStandard, setSelectedStandard] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingMapping, setDeletingMapping] = useState(null);
  const [standardSuppliers, setStandardSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);

  usePageTitle("Settings");

  const loadMappings = useCallback(async () => {
    const data = await apiGet("/supplier-mappings");
    const m = data?.mappings;
    setMappings(m && typeof m === "object" && !Array.isArray(m) ? m : {});
  }, []);

  const loadUnmappedSuppliers = useCallback(async () => {
    const data = await apiGet("/unmapped-suppliers");
    setUnmappedSuppliers(Array.isArray(data?.unmapped_suppliers) ? data.unmapped_suppliers : []);
  }, []);

  const loadStandardSuppliers = useCallback(async () => {
    setSuppliersLoading(true);
    const data = await apiGet("/standard-suppliers");
    setStandardSuppliers(Array.isArray(data?.suppliers) ? data.suppliers : []);
    setSuppliersLoading(false);
  }, []);

  useEffect(() => {
    loadMappings();
    loadUnmappedSuppliers();
    loadStandardSuppliers();
  }, [loadMappings, loadUnmappedSuppliers, loadStandardSuppliers]);

  const handleAddMapping = async () => {
    const rawName = manualRawName.trim();
    if (!rawName || !selectedStandard) {
      toast.error("Please enter both supplier name and select standard mapping");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/supplier-mappings`, null, { params: { raw_name: rawName, standard_name: selectedStandard } });
      toast.success(`Mapped "${rawName}" to ${selectedStandard}`);
      setManualRawName("");
      setSelectedStandard("");
      loadMappings();
      loadUnmappedSuppliers();
    } catch (e) {
      toast.error("Failed to add mapping");
    } finally { setLoading(false); }
  };

  const handleDeleteMapping = async (rawName) => {
    try {
      setDeletingMapping(rawName);
      await axios.delete(`${API}/supplier-mappings/${encodeURIComponent(rawName)}`);
      toast.success("Mapping deleted");
      loadMappings();
      loadUnmappedSuppliers();
    } catch (e) { toast.error("Failed to delete mapping"); }
    finally { setDeletingMapping(null); }
  };

  const sortedMappingEntries = useMemo(() =>
    Object.entries(mappings || {}).sort((a, b) => a[0].localeCompare(b[0])),
    [mappings]
  );

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Configure global settings and supplier mappings" />

      <div className="settings-layout" style={{ display: "grid", gridTemplateColumns: "260px minmax(0, 1fr)", gap: 24, alignItems: "start" }}>
        {/* Left section nav */}
        <nav aria-label="Settings sections" className="settings-sidebar" style={{ background: "var(--color-bg-hemocyanin)", border: "1px solid var(--hairline)", borderRadius: 14, padding: 10, position: "sticky", top: 80 }}>
          {SETTINGS_GROUPS.map(({ group, items }, gi) => (
            <div key={group} style={{ marginTop: gi ? 14 : 0 }}>
              <div style={{ padding: "4px 14px 6px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-4)", opacity: 0.7 }}>{group}</div>
              {items.map(([key, label]) => {
                const on = section === key;
                return (
                  <button type="button"
                    key={key}
                    aria-current={on ? "page" : undefined}
                    data-testid={`settings-nav-${key}`}
                    onClick={() => setSection(key)}
                    style={{ border: 0, width: "100%", textAlign: "left", padding: "9px 14px", borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: "pointer", marginBottom: 1, background: on ? "var(--color-primary-16)" : "transparent", color: on ? "var(--color-primary-500)" : "var(--color-text-2)" }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Right content panel — one section at a time */}
        <div className="settings-content" style={{ minWidth: 0 }}>
          {section === "standard-suppliers" && <StandardSuppliersCard />}
          {section === "threshold" && <OverchargeThresholdCard />}
          {section === "fx" && <SupplierFxCard />}
          {section === "indexes" && <MarketIndexCard />}
          {section === "exclusion" && <ExclusionCard />}
          {section === "sku" && <IcmListCard />}
          {section === "sku-aliases" && <SkuAliasesCard />}
          {section === "reminder" && <InvoiceReminderCard />}
          {section === "price-expiry" && <PriceExpiryReminderCard />}
          {section === "price-drift" && <PriceDriftAlertCard />}
          {section === "manager-digest" && <ManagerDigestCard />}
          {section === "board-pack" && <BoardPackCard />}
          {section === "supplier-confirmation" && <SupplierConfirmationCard />}
          {section === "supplier-map" && <SupplierMappingCard />}
          {section === "forecast" && <InstallForecastCard />}
          {section === "product-map" && <ProductTypeMapCard />}
          {section === "variant-map" && <VariantMapCard />}
          {section === "system-status" && <SystemStatusCard />}
          {section === "review-preview" && <ReviewQueuePreviewCard />}
          {section === "milestone" && <MilestoneCard />}
          {section === "engineer-regions" && <EngineerRegionsCard />}
          {section === "feedback-sync" && <FeaturebaseCard />}
          {section === "ss-supplier-map" && <SupplierScorecardMapCard />}
          {section === "reduction-alerts" && <PriceReductionSettingsCard />}
          {section === "platform-bom" && <PlatformBomCard />}
          {section === "budget-drivers" && <InflationDriversCard />}
          {section === "budget-volumes" && <BudgetVolumesCard />}
          {section === "recompute" && <RecomputeCard />}
          {section === "export" && <DatabaseExportCard />}

          {section === "mappings" && (
            <>
      {/* Unmapped Warning */}
      {unmappedSuppliers.length > 0 && (
        <div className="card" style={{
          marginBottom: "2rem",
          background: "rgba(255, 216, 79, 0.08)",
          border: "1px solid rgba(255, 216, 79, 0.3)"
        }}>
          <h3 style={{ fontWeight: "600", marginBottom: "0.5rem", color: "#FFD84F", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Info size={18} /> {unmappedSuppliers.length} Unmapped Suppliers Found
          </h3>
          <p style={{ color: "var(--octopus-voltage)", fontSize: "0.875rem", opacity: 0.8 }}>
            These suppliers from your invoices don't match the standard list. Map them below for accurate comparisons.
          </p>
        </div>
      )}

      {/* Add Mapping */}
      <div className="card" style={{ marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "1.25rem", color: "var(--octopus-ice)" }}>
          Add Supplier Mapping
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "1rem", alignItems: "end" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Invoice Supplier Name</label>
            <input
              type="text" className="form-input"
              placeholder="e.g., CITY ELECTRICAL FACTORS"
              value={manualRawName}
              onChange={(e) => setManualRawName(e.target.value)}
              data-testid="raw-supplier-input"
            />
            <p style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", marginTop: "0.25rem", opacity: 0.6 }}>
              Exact name as it appears in invoice files
            </p>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Map to Standard Supplier</label>
            {suppliersLoading ? (
              <div className="form-input" style={{ opacity: 0.5 }}>Loading suppliers...</div>
            ) : (
              <Select value={selectedStandard || undefined} onValueChange={(val) => setSelectedStandard(val)}>
                <SelectTrigger
                  className="form-input"
                  data-testid="standard-supplier-select"
                  style={{ height: "auto", padding: "0.6rem 0.75rem", border: "1px solid rgba(96, 240, 248, 0.2)", background: "rgba(10, 18, 42, 0.8)", color: selectedStandard ? "var(--octopus-ice)" : "var(--octopus-voltage)" }}
                >
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent style={{ background: "rgba(10, 18, 42, 0.98)", border: "1px solid rgba(96, 240, 248, 0.2)", backdropFilter: "blur(12px)" }}>
                  {standardSuppliers.map(s => (
                    <SelectItem key={s} value={s} style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", marginTop: "0.25rem", opacity: 0.6 }}>
              {standardSuppliers.length} standard suppliers
            </p>
          </div>
          <Button
            data-testid="add-mapping-btn"
            onClick={handleAddMapping}
            disabled={loading || !manualRawName.trim() || !selectedStandard}
            className="btn btn-primary"
            style={{ marginBottom: "1.5rem" }}
          >
            <Plus size={16} style={{ marginRight: "0.25rem" }} /> Add
          </Button>
        </div>
      </div>

      {/* Current Mappings */}
      <div className="card" style={{ marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "1.25rem", color: "var(--octopus-ice)" }}>
          Current Supplier Mappings {mappings !== null ? `(${Object.keys(mappings).length})` : ""}
        </h2>
        {mappings === null ? (
          <div className="loading-indicator" style={{ padding: "2rem" }}>Loading mappings...</div>
        ) : Object.keys(mappings).length === 0 ? (
          <div className="empty-state" style={{ padding: "2rem" }}>
            <p className="empty-state-text">No mappings yet. Add mappings above to normalize supplier names.</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice Supplier Name</th>
                  <th>Maps To</th>
                  <th style={{ width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedMappingEntries.map(([rawName, standardName]) => (
                  <tr key={rawName}>
                    <td style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{rawName}</td>
                    <td><span className="badge badge-success">{standardName}</span></td>
                    <td>
                      <button
                        onClick={() => handleDeleteMapping(rawName)}
                        disabled={deletingMapping === rawName}
                        style={{
                          background: "none", border: "none", cursor: deletingMapping === rawName ? "wait" : "pointer",
                          color: "var(--octopus-pink)", padding: "0.25rem",
                          opacity: deletingMapping === rawName ? 0.4 : 1,
                          transition: "opacity 0.15s ease"
                        }}
                        title="Delete mapping"
                        data-testid={`delete-mapping-${rawName}`}
                      >
                        {deletingMapping === rawName ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <Trash2 size={16} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="card" style={{
        background: "rgba(96, 240, 248, 0.05)",
        border: "1px solid rgba(96, 240, 248, 0.2)"
      }}>
        <h3 style={{ fontWeight: "600", marginBottom: "0.75rem", color: "var(--octopus-voltage)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Info size={18} /> How Supplier Mapping Works
        </h3>
        <ul style={{ color: "var(--octopus-ice)", fontSize: "0.875rem", paddingLeft: "1.5rem", lineHeight: "1.8", opacity: 0.8 }}>
          <li>Upload invoices with any supplier names</li>
          <li>System detects unmapped suppliers automatically</li>
          <li>Map invoice supplier names to your standard supplier list</li>
          <li>Future uploads will use the mapped names for matching</li>
          <li>Re-upload invoices after adding mappings to apply changes</li>
        </ul>
      </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;


/**
 * Transport / freight exclusion. An exact-word list + master toggle. When on,
 * invoice lines whose description contains any of these words (whole-word,
 * case-insensitive) are excluded from every dashboard, analytic and report —
 * the data is never deleted, just filtered. Changing it recomputes analytics.
 */
function SupplierFxCard() {
  const [globals, setGlobals] = useState({ max_tolerance_pct: 8, default_fx_source: "ecb" });
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState({ supplier: "", base_currency: "USD", base_tolerance: "", max_tolerance: "", fx_source: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const s = await apiGet("/settings").catch(() => null);
    if (s) setGlobals({ max_tolerance_pct: s.max_tolerance_pct ?? 8, default_fx_source: s.default_fx_source ?? "ecb" });
    const c = await apiGet("/supplier-fx-config").catch(() => null);
    setConfigs(Array.isArray(c?.configs) ? c.configs : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveGlobals = async () => {
    try {
      await axios.put(`${API}/settings`, null, { params: { max_tolerance_pct: Number(globals.max_tolerance_pct), default_fx_source: globals.default_fx_source } });
      toast.success("FX defaults saved");
    } catch { toast.error("Failed to save FX defaults"); }
  };

  const addConfig = async () => {
    if (!form.supplier.trim()) { toast.error("Supplier is required"); return; }
    if (!/^[A-Za-z]{3}$/.test(form.base_currency.trim())) { toast.error("Currency must be a 3-letter code"); return; }
    setSaving(true);
    try {
      const body = { supplier: form.supplier.trim(), base_currency: form.base_currency.trim().toUpperCase() };
      if (form.base_tolerance !== "") body.base_tolerance = Number(form.base_tolerance);
      if (form.max_tolerance !== "") body.max_tolerance = Number(form.max_tolerance);
      if (form.fx_source.trim()) body.fx_source = form.fx_source.trim();
      await axios.put(`${API}/supplier-fx-config`, body);
      toast.success("Supplier FX config saved");
      setForm({ supplier: "", base_currency: "USD", base_tolerance: "", max_tolerance: "", fx_source: "" });
      await load();
    } catch { toast.error("Failed to save supplier config"); } finally { setSaving(false); }
  };

  const del = async (supplier) => {
    try { await axios.delete(`${API}/supplier-fx-config/${encodeURIComponent(supplier)}`); await load(); toast.success("Removed"); }
    catch { toast.error("Failed to remove"); }
  };

  const inp = { padding: "0.4rem 0.55rem", borderRadius: "6px", background: "rgba(96,240,248,0.05)", border: "1px solid rgba(96,240,248,0.15)", color: "var(--octopus-ice)", fontSize: "0.82rem" };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="fx-config-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "0.4rem", color: "var(--octopus-ice)" }}>Supplier Currency &amp; FX-Adjusted Variance</h2>
      <p style={{ fontSize: "0.82rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        Suppliers pricing in a foreign currency get their variance tolerance widened by the FX move between the price-file month and the invoice date. GBP / unlisted suppliers are unaffected.
      </p>

      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "1.25rem" }}>
        <label style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)" }}>Max effective tolerance (%)
          <div><input type="number" step="0.5" value={globals.max_tolerance_pct} onChange={(e) => setGlobals({ ...globals, max_tolerance_pct: e.target.value })} style={{ ...inp, width: 110 }} data-testid="fx-max-tol" /></div>
        </label>
        <label style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)" }}>Default FX source
          <div><input value={globals.default_fx_source} onChange={(e) => setGlobals({ ...globals, default_fx_source: e.target.value })} style={{ ...inp, width: 150 }} placeholder="ecb" data-testid="fx-source" /></div>
        </label>
        <Button className="btn btn-secondary" onClick={saveGlobals}>Save defaults</Button>
      </div>

      <table className="data-table" style={{ marginBottom: "1rem" }}>
        <thead><tr><th>Supplier</th><th>Currency</th><th>Base tol %</th><th>Max tol %</th><th>Source</th><th></th></tr></thead>
        <tbody>
          {configs.length === 0 && <tr><td colSpan={6} style={{ opacity: 0.5, fontSize: "0.8rem" }}>No supplier currency overrides yet.</td></tr>}
          {configs.map((c) => (
            <tr key={c.supplier} data-testid={`fx-row-${c.supplier}`}>
              <td style={{ fontWeight: 600, color: "var(--octopus-ice)" }}>{c.supplier}</td>
              <td>{c.base_currency}</td>
              <td>{c.base_tolerance ?? "—"}</td>
              <td>{c.max_tolerance ?? "—"}</td>
              <td>{c.fx_source ?? "default"}</td>
              <td><button onClick={() => del(c.supplier)} title="Remove" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--octopus-pink)" }}><Trash2 size={15} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
        <input placeholder="Supplier (exact)" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} style={{ ...inp, width: 170 }} data-testid="fx-new-supplier" />
        <input placeholder="USD" value={form.base_currency} onChange={(e) => setForm({ ...form, base_currency: e.target.value })} style={{ ...inp, width: 70 }} />
        <input placeholder="base %" value={form.base_tolerance} onChange={(e) => setForm({ ...form, base_tolerance: e.target.value })} style={{ ...inp, width: 80 }} />
        <input placeholder="max %" value={form.max_tolerance} onChange={(e) => setForm({ ...form, max_tolerance: e.target.value })} style={{ ...inp, width: 80 }} />
        <input placeholder="fx source (opt)" value={form.fx_source} onChange={(e) => setForm({ ...form, fx_source: e.target.value })} style={{ ...inp, width: 120 }} />
        <Button className="btn btn-primary" onClick={addConfig} disabled={saving} data-testid="fx-add-btn">
          {saving ? <Loader2 size={14} style={{ animation: "spin 0.8s linear infinite" }} /> : <Plus size={14} />} Add / update
        </Button>
      </div>
    </div>
  );
}

function MilestoneCard() {
  // TV-only "OSS overtakes LPO" celebration: banner across the top of the TV
  // view + fireworks. Durations configurable; simulate/stop for demos.
  const [status, setStatus] = useState(null);
  const [bannerDays, setBannerDays] = useState("7");
  const [fireworksMins, setFireworksMins] = useState("60");
  const [saving, setSaving] = useState(false);
  const [simBusy, setSimBusy] = useState(false);

  const load = useCallback(async () => {
    const m = await apiGet("/supply-scanner/milestone");
    if (m) {
      setStatus(m);
      if (m.settings) {
        setBannerDays(String(Math.round((m.settings.banner_seconds / 86400) * 100) / 100));
        setFireworksMins(String(Math.round(m.settings.fireworks_seconds / 60)));
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const days = parseFloat(bannerDays);
    const mins = parseFloat(fireworksMins);
    if (!(days > 0) || !(mins > 0)) { toast.error("Durations must be positive numbers"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/supply-scanner/milestone/settings`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ banner_seconds: Math.round(days * 86400), fireworks_seconds: Math.round(mins * 60) }),
      });
      if (!res.ok) throw new Error((await res.json())?.detail || `HTTP ${res.status}`);
      toast.success("Milestone settings saved");
      load();
    } catch (e) { toast.error(String(e.message || "Failed to save settings")); }
    finally { setSaving(false); }
  };

  const simulate = async (action) => {
    setSimBusy(true);
    try {
      const res = await fetch(`${API}/supply-scanner/milestone/simulate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(action === "start" ? "Simulation started — open the TV view" : "Simulation stopped");
      load();
    } catch { toast.error("Simulation request failed"); }
    finally { setSimBusy(false); }
  };

  const inputStyle = { width: 110, background: "var(--color-bg-siphon)", border: "1px solid var(--hairline)",
    borderRadius: 8, padding: "8px 10px", color: "var(--color-text-1)", fontSize: 14 };
  const lblStyle = { fontSize: 13, color: "var(--color-text-3)", marginBottom: 6, display: "block" };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="milestone-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <PartyPopper size={18} /> TV Milestone Celebration
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        When weekly OSS orders sustainably overtake LPO, the TV view celebrates: a banner across the top
        and fireworks. Only the TV view is affected. Detection is automatic — durations below control how
        long each part runs from the moment the crossover is detected.
      </p>

      <div style={{ display: "flex", gap: 24, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <label style={lblStyle}>Banner duration (days)</label>
          <input type="number" min="0.01" step="0.5" value={bannerDays} onChange={(e) => setBannerDays(e.target.value)}
            style={inputStyle} data-testid="milestone-banner-days" />
        </div>
        <div>
          <label style={lblStyle}>Fireworks duration (minutes)</label>
          <input type="number" min="1" step="5" value={fireworksMins} onChange={(e) => setFireworksMins(e.target.value)}
            style={inputStyle} data-testid="milestone-fireworks-mins" />
        </div>
        <Button onClick={save} disabled={saving} className="btn btn-primary" data-testid="milestone-save">
          {saving ? "Saving…" : "Save durations"}
        </Button>
      </div>

      <div style={{ borderTop: "1px solid var(--hairline)", paddingTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Button onClick={() => simulate("start")} disabled={simBusy} className="btn btn-secondary" data-testid="milestone-simulate">
          Simulate on TV
        </Button>
        <Button onClick={() => simulate("stop")} disabled={simBusy || !status?.simulating} className="btn btn-secondary" data-testid="milestone-stop-sim">
          Stop simulation
        </Button>
        <span style={{ fontSize: 12.5, color: "var(--color-text-3)" }} data-testid="milestone-status">
          {status ? (
            status.banner_active || status.fireworks_active
              ? `Celebration ACTIVE (${status.source}) — banner ${status.banner_active ? "on" : "off"}, fireworks ${status.fireworks_active ? "on" : "off"}`
              : status.crossover_week
                ? `Crossover detected (w/c ${status.week_label}) — celebration window has ended`
                : "No crossover yet — OSS hasn't sustainably overtaken LPO"
          ) : "Loading status…"}
        </span>
      </div>
    </div>
  );
}


function RecomputeCard() {
  // Polls /admin/recompute/status while a recompute runs so the user can watch
  // progress. A recompute FORCE re-matches every stored invoice against the
  // current matcher/parse code — needed because dashboard/analytics numbers are
  // cached, so a code change isn't reflected until this runs.
  const [status, setStatus] = useState(null); // {state, processed, total, percent, message, ...}
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const fmtTime = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const poll = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/recompute/status`);
      if (!res.ok) return;
      const s = await res.json();
      setStatus(s);
      if (s.state !== "running") {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch { /* transient — keep polling */ }
  }, []);

  // On mount, check whether a recompute is already running (e.g. started
  // elsewhere or a page reload mid-run) and resume the progress bar.
  useEffect(() => {
    poll();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [poll]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(poll, 1500);
  }, [poll]);

  const start = async () => {
    if (starting || status?.state === "running") return;
    setStarting(true);
    try {
      const res = await fetch(`${API}/admin/recompute`, { method: "POST" });
      if (res.status === 409) {
        toast.message("A recompute is already running");
        await poll(); startPolling();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const s = await res.json();
      setStatus({ ...s, state: "running" });
      toast.success("Recompute started");
      startPolling();
    } catch (e) {
      toast.error("Failed to start recompute");
    } finally {
      setStarting(false);
    }
  };

  const running = status?.state === "running";
  const pct = running ? (status?.percent ?? 0) : (status?.state === "done" ? 100 : 0);

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="recompute-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <RefreshCw size={18} /> Recompute Analytics
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        Re-runs the matching engine against every stored invoice line and rebuilds all dashboard figures.
        Dashboard numbers are cached, so run this after a matcher or price/invoice change to apply it everywhere — otherwise a report may show a line priced while the dashboard still shows it unpriced.
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <Button
          className="btn btn-primary"
          data-testid="recompute-btn"
          onClick={start}
          disabled={running || starting}
        >
          {(running || starting)
            ? <Loader2 size={15} style={{ animation: "spin 0.8s linear infinite", marginRight: "0.4rem" }} />
            : <RefreshCw size={15} style={{ marginRight: "0.4rem" }} />}
          {running ? "Recomputing…" : starting ? "Starting…" : "Recompute now"}
        </Button>

        {status?.state === "done" && status?.computed_at && (
          <span style={{ fontSize: "0.8rem", color: "#4ADE80", display: "flex", alignItems: "center", gap: "0.35rem" }}>
            <CheckCircle2 size={15} /> Last recompute {fmtTime(status.computed_at)}
            {status.duration_seconds != null ? ` (${status.duration_seconds}s)` : ""}
          </span>
        )}
        {status?.state === "error" && (
          <span style={{ fontSize: "0.8rem", color: "#F87171", display: "flex", alignItems: "center", gap: "0.35rem" }}>
            <AlertTriangle size={15} /> {status.message || "Recompute failed"}
          </span>
        )}
      </div>

      {(running || status?.state === "done") && (
        <div style={{ marginTop: "1rem" }}>
          <div style={{
            height: "10px", borderRadius: "6px", overflow: "hidden",
            background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)",
          }}>
            <div style={{
              height: "100%", width: `${pct}%`,
              background: status?.state === "done" ? "#4ADE80" : "var(--octopus-ice)",
              transition: "width 0.4s ease",
            }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.4rem", fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.7 }}>
            <span>{running ? (status?.message || "Working…") : "Complete"}</span>
            <span>
              {status?.total ? `${(status.processed || 0).toLocaleString()} / ${status.total.toLocaleString()}` : ""} {pct ? `· ${pct}%` : ""}
            </span>
          </div>
        </div>
      )}

      <div style={{ borderTop: "1px solid var(--hairline)", marginTop: "1.5rem", paddingTop: "1.25rem" }}>
        <h3 style={{ fontSize: "0.95rem", fontWeight: 600, color: "var(--octopus-ice)", marginBottom: "0.4rem" }}>Recommit approved submissions to price files</h3>
        <p style={{ fontSize: "0.82rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "0.9rem", maxWidth: 640 }}>
          One-off backfill for submissions approved before prices were written to the price file. Writes each approved price into <code>price_files</code> for its effective month, so the internal price comparison shows the agreed price. Safe to re-run.
        </p>
        <RecommitButton />
      </div>
    </div>
  );
}

function RecommitButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/portal/admin/recommit-price-files`);
      const d = res.data || {};
      toast.success(`Committed ${d.rows_committed ?? 0} prices from ${d.submissions ?? 0} approved submission(s).`);
    } catch (e) { toast.error(e.response?.data?.detail || "Recommit failed."); }
    finally { setBusy(false); }
  };
  return <button className="btn btn-secondary" onClick={run} disabled={busy}>{busy ? "Committing…" : "Recommit now"}</button>;
}

function IcmListCard() {
  const [info, setInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    apiGet("/portal/admin/warehouse-skus").then(setInfo).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/portal/admin/warehouse-skus`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`ICM list updated — ${res.data.count.toLocaleString()} SKUs`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear the ICM list? Filtering turns off: the internal price comparison shows all items and every approved item creates an Asana task.")) return;
    try {
      await axios.delete(`${API}/portal/admin/warehouse-skus`);
      toast.success("ICM list cleared — filtering off");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Clear failed"); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="icm-list-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Boxes size={18} /> ICM / Warehouse SKU list
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        The parts we hold. It scopes the <strong>Internal Price Comparison</strong> to only items on this list, and drives the Asana submission filter (only listed parts reach the records updater). Upload a CSV with part codes in <strong>column A</strong>; each upload replaces the whole list. With no list, both filters are off (all items compared, all approvals reach Asana).
      </p>
      <div style={{
        padding: "0.65rem 0.9rem", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem",
        background: info?.configured ? "rgba(34,197,94,0.07)" : "rgba(255,216,79,0.07)",
        border: `1px solid ${info?.configured ? "rgba(34,197,94,0.3)" : "rgba(255,216,79,0.35)"}`,
        color: "var(--octopus-ice)",
      }} data-testid="icm-list-status">
        {info == null ? "Loading…" : info.configured ? (
          <><strong>{info.count.toLocaleString()}</strong> SKUs on the list
            {info.filename && <> · from <span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{info.filename}</span></>}
            {info.updated_at && <> · updated {new Date(info.updated_at).toLocaleString()}</>}</>
        ) : (<>No list uploaded — filtering is <strong>off</strong>.</>)}
      </div>
      <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: "none" }} data-testid="icm-file-input" />
      <div style={{ display: "flex", gap: "0.75rem" }}>
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn btn-primary" data-testid="icm-upload-btn">
          <Upload size={13} style={{ marginRight: "0.35rem" }} />
          {uploading ? "Uploading…" : info?.configured ? "Replace list (CSV)" : "Upload list (CSV)"}
        </Button>
        {info?.configured && (
          <Button onClick={handleClear} className="btn btn-danger" data-testid="icm-clear-btn">
            <Trash2 size={13} style={{ marginRight: "0.35rem" }} /> Clear list
          </Button>
        )}
      </div>
    </div>
  );
}

function PriceReductionSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    apiGet("/settings/price-reductions").then((d) => setCfg(d?.config || null)).catch(() => setCfg(null));
  }, []);

  const fields = [
    ["min_pct_below", "Min % below price file", "A line only counts when invoiced this far under the file price."],
    ["min_invoices", "Min invoices", "How many invoices at the lower price before it's treated as the new rate."],
    ["lookback_months", "Lookback (months)", "Only invoices this recent are considered."],
    ["max_spread_pct", "Max price spread %", "How tightly the low prices must cluster (higher = looser)."],
    ["min_confidence", "Min confidence", "Hide alerts below this confidence score (0 to 100)."],
  ];

  const save = async () => {
    setSaving(true);
    try {
      const qs = fields.map(([k]) => `${k}=${encodeURIComponent(cfg[k])}`).join("&");
      await axios.put(`${API}/settings/price-reductions?${qs}`);
      toast.success("Reduction alert thresholds saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save thresholds.");
    } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="reduction-alerts-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <AlertTriangle size={18} /> Reduction alert thresholds
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        Controls the "Uncaptured price reductions" list on the dashboard: SKUs invoiced consistently below the price file, where a negotiated reduction was never submitted. Changes apply on the next recompute.
      </p>
      {cfg == null ? <div style={{ color: "var(--octopus-voltage)", opacity: 0.7 }}>Loading…</div> : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            {fields.map(([k, label, help]) => (
              <div key={k} className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{label}</label>
                <input type="number" className="form-input" value={cfg[k]}
                  onChange={(e) => setCfg({ ...cfg, [k]: e.target.value === "" ? "" : Number(e.target.value) })} />
                <p style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", opacity: 0.6, marginTop: "0.25rem" }}>{help}</p>
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="btn btn-primary">{saving ? "Saving…" : "Save thresholds"}</Button>
        </>
      )}
    </div>
  );
}

function SkuAliasesCard() {
  const [meta, setMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    apiGet("/sku-aliases/meta").then(setMeta).catch(() => setMeta({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/sku-aliases/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`SKU aliases updated: ${res.data.count.toLocaleString()} across ${res.data.suppliers} suppliers`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const configured = meta && meta.count;
  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="sku-aliases-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Boxes size={18} /> SKU aliases
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        Cross-reference codes that mean the same product in different systems (per supplier), e.g. an ECA code and a Warm code. The <strong>price file generator</strong> shows each SKU's equivalent code in an <strong>AKA</strong> column next to it. It's display-only: every SKU keeps its own row and price history. Upload a CSV with <strong>supplier</strong>, <strong>sku</strong> and <strong>aka</strong> columns, one pair per row. Each upload replaces the whole map.
      </p>
      <div style={{
        padding: "0.65rem 0.9rem", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem",
        background: configured ? "rgba(34,197,94,0.07)" : "rgba(255,216,79,0.07)",
        border: `1px solid ${configured ? "rgba(34,197,94,0.3)" : "rgba(255,216,79,0.35)"}`,
        color: "var(--octopus-ice)",
      }} data-testid="sku-aliases-status">
        {meta == null ? "Loading…" : configured ? (
          <><strong>{meta.count.toLocaleString()}</strong> alias entries mapped
            {meta.filename && <> from <span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{meta.filename}</span></>}
            {meta.uploadedAt && <> · updated {new Date(meta.uploadedAt).toLocaleString()}</>}</>
        ) : (<>No aliases uploaded yet.</>)}
      </div>
      <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: "none" }} data-testid="sku-aliases-file-input" />
      <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn btn-primary" data-testid="sku-aliases-upload-btn">
        <Upload size={13} style={{ marginRight: "0.35rem" }} />
        {uploading ? "Uploading…" : configured ? "Replace aliases (CSV)" : "Upload aliases (CSV)"}
      </Button>
    </div>
  );
}

// Maps the order-data supplier names (e.g. "City Plumbing Supplies") onto the
// Featurebase supplier labels (e.g. "CPS") so the supplier scorecard joins order
// metrics to feedback. Pre-filled with heuristic suggestions; save to lock them in.
function SupplierScorecardMapCard() {
  const [data, setData] = useState(null);
  const [map, setMap] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    apiGet("/supply-scanner/supplier-map").then((d) => {
      setData(d);
      const init = {};
      (d?.orderSuppliers || []).forEach((o) => { init[o] = (d.map && d.map[o]) || d.suggestions?.[o] || ""; });
      setMap(init);
    }).catch(() => setData({ orderSuppliers: [], feedbackSuppliers: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const applySuggestions = () => setMap((m) => {
    const next = { ...m };
    Object.entries(data?.suggestions || {}).forEach(([o, c]) => { next[o] = c; });
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(map).forEach(([o, c]) => { if ((c || "").trim()) clean[o] = c; });
      const res = await axios.put(`${API}/supply-scanner/supplier-map`, { map: clean });
      toast.success(`Supplier mapping saved (${res.data.count} mapped).`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save mapping"); }
    finally { setSaving(false); }
  };

  const orderSuppliers = data?.orderSuppliers || [];
  const fb = data?.feedbackSuppliers || [];
  const unmapped = orderSuppliers.filter((o) => !(map[o] || "").trim()).length;

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="ss-supplier-map-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Supplier Mapping</h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.8, marginBottom: "1rem", maxWidth: 660 }}>
        The order data names suppliers in full (e.g. <strong>City Plumbing Supplies</strong>) while Featurebase feedback uses short labels (e.g. <strong>CPS</strong>). Map each order supplier onto its feedback label so the scorecard joins orders to feedback. Suggestions are pre-filled where the names line up; confirm or correct, then save.
      </p>
      {data == null ? <p style={{ opacity: 0.6 }}>Loading…</p> : orderSuppliers.length === 0 ? (
        <p style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.85rem" }}>No order suppliers found yet. Refresh the Supply Scanner data first.</p>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.8rem", color: "var(--octopus-voltage)", opacity: 0.8 }}>{orderSuppliers.length} order suppliers · <strong style={{ color: unmapped ? "#fbbf24" : "#86efac" }}>{unmapped}</strong> unmapped</span>
            {Object.keys(data?.suggestions || {}).length > 0 && (
              <button className="btn btn-secondary" style={{ padding: "0.25rem 0.7rem", fontSize: "0.78rem" }} onClick={applySuggestions}>Apply {Object.keys(data.suggestions).length} suggestion(s)</button>
            )}
          </div>
          <div style={{ border: "1px solid rgba(96,240,248,0.12)", borderRadius: 8, overflow: "hidden" }}>
            {orderSuppliers.map((o, i) => (
              <div key={o} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "0.8rem", alignItems: "center", padding: "0.5rem 0.8rem", borderTop: i ? "1px solid rgba(96,240,248,0.08)" : "none" }}>
                <span style={{ fontSize: "0.85rem", color: "var(--octopus-ice)", fontWeight: 500 }}>{o}
                  {!(map[o] || "").trim() && data.suggestions?.[o] && <span style={{ fontSize: "0.72rem", color: "#fbbf24", marginLeft: 6 }}>→ {data.suggestions[o]}?</span>}
                </span>
                <select className="form-input" style={{ fontSize: "0.82rem", padding: "0.35rem 0.5rem" }} value={map[o] || ""} onChange={(e) => setMap((m) => ({ ...m, [o]: e.target.value }))} data-testid={`ss-map-${o}`}>
                  <option value="">— no feedback match —</option>
                  {fb.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: "0.6rem", marginTop: "1rem" }}>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save mapping"}</button>
          </div>
        </>
      )}
    </div>
  );
}

// Featurebase feedback sync status for the Supply Scanner supplier scorecard.
// The key lives in the backend (FEATUREBASE_API_KEY); here we just show the
// connection state and offer a manual pull. The tool also syncs hourly.
function FeaturebaseCard() {
  const [meta, setMeta] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    apiGet("/supply-scanner/feedback/meta").then(setMeta).catch(() => setMeta({ configured: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/supply-scanner/feedback/sync`, {});
      toast.success("Sync started — it runs in the background. Refreshing status shortly.");
      // The pull runs in the background; refresh the status a few times.
      let tries = 20;
      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/supply-scanner/feedback/meta`);
          setMeta(data);
          if (!data.running && tries < 20) {
            if (data.lastError) toast.error(`Sync failed: ${data.lastError}`);
            setSyncing(false);
            return;
          }
        } catch { /* keep polling */ }
        if (tries-- > 0) setTimeout(poll, 3000); else setSyncing(false);
      };
      setTimeout(poll, 2500);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start sync"); setSyncing(false); }
  };

  const configured = meta?.configured;
  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="featurebase-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Supplier Feedback (Featurebase)</h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.8, marginBottom: "1rem", maxWidth: 660 }}>
        Pulls product feedback from Featurebase, tagged by supplier, theme and cause, for the Supply Scanner supplier scorecard. The API key is set in the backend (FEATUREBASE_API_KEY); it syncs hourly, and you can pull now. Read-only.
      </p>
      <div style={{
        padding: "0.65rem 0.9rem", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem",
        background: configured ? "rgba(34,197,94,0.07)" : "rgba(255,216,79,0.07)",
        border: `1px solid ${configured ? "rgba(34,197,94,0.3)" : "rgba(255,216,79,0.35)"}`,
        color: "var(--octopus-ice)",
      }}>
        {meta == null ? "Loading…" : !configured ? (
          <>Not connected. Add <span style={{ fontFamily: "monospace" }}>FEATUREBASE_API_KEY</span> in the backend, then sync.</>
        ) : (
          <><strong>{(meta.count || 0).toLocaleString()}</strong> posts · {meta.suppliers?.length || 0} suppliers · {meta.themes?.length || 0} themes
            {meta.lastSync && <> · last synced {new Date(meta.lastSync).toLocaleString("en-GB")}</>}</>
        )}
      </div>
      <Button onClick={sync} disabled={syncing || !configured} className="btn btn-primary" data-testid="featurebase-sync-btn">
        <RefreshCw size={13} style={{ marginRight: "0.35rem" }} />
        {syncing ? "Syncing…" : "Sync now"}
      </Button>
    </div>
  );
}

// The platform BOM feeds the Budget Modeller (parts + avg quantity per install
// per variant). It lives here in Settings rather than on the modeller pages so
// the two BOM concepts (the loaded data source vs a per-scenario build) don't
// get confused.
function PlatformBomCard() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    apiGet("/qbr/cost-per-install").then(setData).catch(() => setData({ loaded: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/qbr/bom/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success(`BOM loaded: ${res.data.jobs?.toLocaleString()} jobs across ${res.data.installTypes?.length} install type(s).`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed. Check it's the dispatch/BOM CSV."); }
    finally { setUploading(false); }
  };

  const loaded = data && data.loaded;
  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="platform-bom-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Boxes size={18} /> Platform BOM
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem", maxWidth: 660 }}>
        The aggregated Bill of Materials that both Price Modelling and the Budget Modeller read: parts and average quantity per install for each variant. Upload the line-level dispatch export (or a simple BOM CSV). Each upload replaces the current one.
      </p>
      <div style={{
        padding: "0.65rem 0.9rem", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem",
        background: loaded ? "rgba(34,197,94,0.07)" : "rgba(255,216,79,0.07)",
        border: `1px solid ${loaded ? "rgba(34,197,94,0.3)" : "rgba(255,216,79,0.35)"}`,
        color: "var(--octopus-ice)",
      }}>
        {data == null ? "Loading…" : loaded ? (
          <><strong>{data.jobs?.toLocaleString()}</strong> jobs · {data.installTypes?.length || 0} install type(s) · {data.variants?.length || 0} variant(s)
            {data.uploadedAt && <> · loaded {new Date(data.uploadedAt).toLocaleDateString("en-GB")}</>}</>
        ) : (<>No BOM loaded yet.</>)}
      </div>
      <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: "none" }} data-testid="platform-bom-input" />
      <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn btn-primary" data-testid="platform-bom-upload-btn">
        <Upload size={13} style={{ marginRight: "0.35rem" }} />
        {uploading ? "Uploading…" : loaded ? "Replace BOM (CSV)" : "Upload BOM (CSV)"}
      </Button>
    </div>
  );
}

// Inflation drivers for the Budget Modeller. The market indices are exposed
// read-only with their derived year-on-year %; General inflation and any custom
// drivers hold a single manual annual %.
function InflationDriversCard() {
  const [general, setGeneral] = useState(0);
  const [custom, setCustom] = useState([]);
  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiGet("/budget/drivers").then((d) => {
      setGeneral(d?.generalPct || 0);
      setCustom(d?.custom || []);
      setIndices((d?.drivers || []).filter((x) => x.kind === "index"));
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRow = (i, key, val) => setCustom((cs) => cs.map((c, j) => j === i ? { ...c, [key]: val } : c));
  const addRow = () => setCustom((cs) => [...cs, { name: "", pct: 0 }]);
  const delRow = (i) => setCustom((cs) => cs.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/budget/drivers`, { generalPct: Number(general) || 0, custom: custom.filter((c) => (c.name || "").trim()).map((c) => ({ ...c, pct: Number(c.pct) || 0 })) });
      toast.success("Inflation drivers saved.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save drivers"); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="budget-drivers-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Inflation Drivers</h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.8, marginBottom: "1rem", maxWidth: 660 }}>
        The rates a BOM line can be tagged with in the Budget Modeller. Set the general rate for most lines, add commodity drivers (e.g. Copper, Lithium) with their own annual %, and the market indexes appear automatically with their year-on-year rate.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1.2rem" }}>
            <span style={{ fontSize: "0.9rem", color: "var(--octopus-ice)", fontWeight: 600, minWidth: 140 }}>General inflation</span>
            <input type="number" step="0.1" value={general} onChange={(e) => setGeneral(e.target.value)} style={{ width: 90, background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 6, padding: "6px 8px", fontSize: "0.85rem", textAlign: "right" }} />
            <span style={{ color: "var(--octopus-voltage)", opacity: 0.7 }}>% / yr</span>
          </div>

          <div style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--octopus-voltage)", opacity: 0.7, marginBottom: "0.5rem" }}>Custom drivers</div>
          {custom.length === 0 && <p style={{ fontSize: "0.82rem", color: "var(--octopus-voltage)", opacity: 0.6, marginBottom: "0.6rem" }}>None yet.</p>}
          {custom.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
              <input value={c.name} placeholder="Driver name (e.g. Copper)" onChange={(e) => setRow(i, "name", e.target.value)} style={{ flex: "1 1 220px", background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 6, padding: "6px 8px", fontSize: "0.85rem" }} />
              <input type="number" step="0.1" value={c.pct} onChange={(e) => setRow(i, "pct", e.target.value)} style={{ width: 90, background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 6, padding: "6px 8px", fontSize: "0.85rem", textAlign: "right" }} />
              <span style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.85rem" }}>%</span>
              <button onClick={() => delRow(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--octopus-voltage)", opacity: 0.7 }}><Trash2 size={15} /></button>
            </div>
          ))}
          <button className="btn btn-secondary" style={{ padding: "0.3rem 0.75rem", fontSize: "0.8rem", marginTop: "0.3rem" }} onClick={addRow}><Plus size={13} style={{ marginRight: 4 }} /> Add driver</button>

          {indices.length > 0 && (
            <div style={{ marginTop: "1.4rem" }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--octopus-voltage)", opacity: 0.7, marginBottom: "0.5rem" }}>Market indexes (auto, read-only)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {indices.map((ix) => (
                  <span key={ix.id} style={{ fontSize: "0.82rem", color: "var(--octopus-ice)", background: "#0f0a1e", border: "1px solid #2a1c47", borderRadius: 6, padding: "4px 10px" }}>
                    {ix.name} <strong style={{ color: ix.pct >= 0 ? "#fbbf24" : "#34d399" }}>{ix.pct >= 0 ? "+" : ""}{ix.pct}%</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: "1.4rem" }}>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save drivers"}</button>
          </div>
        </>
      )}
    </div>
  );
}

// Per-variant annual install volume: the weighting behind the Budget Modeller's
// portfolio blend and annualised category spend.
function BudgetVolumesCard() {
  const [variants, setVariants] = useState([]);
  const [volumes, setVolumes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiGet("/budget/variant-forecast").then((d) => {
      setVariants(d?.variants || []);
      setVolumes(d?.volumes || {});
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setVol = (v, val) => setVolumes((m) => ({ ...m, [v]: val }));
  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(volumes).forEach(([k, v]) => { if (Number(v) > 0) clean[k] = Number(v); });
      await axios.put(`${API}/budget/variant-forecast`, { volumes: clean });
      toast.success("Budget volumes saved.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save volumes"); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="budget-volumes-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Budget Volumes</h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.8, marginBottom: "1rem", maxWidth: 660 }}>
        Projected annual installs per variant. The Budget Modeller uses these to blend the average uplift per install across the portfolio and to annualise category spend.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : variants.length === 0 ? (
        <p style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.85rem" }}>No variants yet. Upload the Platform BOM first.</p>
      ) : (
        <>
          <div style={{ border: "1px solid rgba(96,240,248,0.12)", borderRadius: 8, overflow: "hidden" }}>
            {variants.map((v, i) => (
              <div key={v.variant} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.9fr", gap: "0.8rem", alignItems: "center", padding: "0.55rem 0.9rem", borderTop: i ? "1px solid rgba(96,240,248,0.08)" : "none" }}>
                <span style={{ fontSize: "0.86rem", color: "var(--octopus-ice)", fontWeight: 500 }}>{v.variant}
                  <span style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", opacity: 0.6, marginLeft: 6 }}>{v.category}</span>
                </span>
                <span style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.7 }}>{(v.jobCount || 0).toLocaleString()} in BOM</span>
                <input type="number" min="0" value={volumes[v.variant] != null ? volumes[v.variant] : ""} placeholder="0" onChange={(e) => setVol(v.variant, e.target.value)}
                  style={{ width: "100%", background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 6, padding: "6px 8px", fontSize: "0.85rem", textAlign: "right" }} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: "1.2rem" }}>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save volumes"}</button>
          </div>
        </>
      )}
    </div>
  );
}

function EngineerRegionsCard() {
  const [meta, setMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    apiGet("/supply-scanner/regions/meta").then(setMeta).catch(() => setMeta({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/supply-scanner/regions/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Engineer regions updated — ${res.data.count.toLocaleString()} engineers`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const configured = meta && meta.count;
  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="engineer-regions-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Upload size={18} /> Engineer regions
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        Maps each engineer's email to their region, so the Supply Scanner <strong>Geography</strong> tab can compare OSS vs LPO by region. Upload a CSV with an <strong>email</strong> column and a <strong>region</strong> (or subregion) column; each upload replaces the whole map.
      </p>
      <div style={{
        padding: "0.65rem 0.9rem", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem",
        background: configured ? "rgba(34,197,94,0.07)" : "rgba(255,216,79,0.07)",
        border: `1px solid ${configured ? "rgba(34,197,94,0.3)" : "rgba(255,216,79,0.35)"}`,
        color: "var(--octopus-ice)",
      }} data-testid="engineer-regions-status">
        {meta == null ? "Loading…" : configured ? (
          <><strong>{meta.count.toLocaleString()}</strong> engineers mapped
            {meta.filename && <> · from <span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{meta.filename}</span></>}
            {meta.uploadedAt && <> · updated {new Date(meta.uploadedAt).toLocaleString()}</>}</>
        ) : (<>No map uploaded — using the bundled seed list until you upload one.</>)}
      </div>
      <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: "none" }} data-testid="engineer-regions-file-input" />
      <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn btn-primary" data-testid="engineer-regions-upload-btn">
        <Upload size={13} style={{ marginRight: "0.35rem" }} />
        {uploading ? "Uploading…" : configured ? "Replace map (CSV)" : "Upload map (CSV)"}
      </Button>
    </div>
  );
}

function StandardSuppliersCard() {
  const [suppliers, setSuppliers] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await apiGet("/standard-suppliers");
      setSuppliers(Array.isArray(d?.suppliers) ? d.suppliers : []);
    } catch { toast.error("Failed to load standard suppliers"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/standard-suppliers`, null, { params: { name } });
      setSuppliers(res.data?.suppliers || suppliers);
      setNewName("");
      toast.success(`Added ${name}`);
    } catch { toast.error("Add failed"); }
    finally { setBusy(false); }
  };

  const remove = async (name) => {
    try {
      // First try without force — the backend guards names that are still in use.
      let res = await axios.delete(`${API}/standard-suppliers/${encodeURIComponent(name)}`);
      if (res.data && res.data.ok === false && res.data.in_use) {
        if (!window.confirm(`${res.data.message}\n\nRemove anyway?`)) return;
        res = await axios.delete(`${API}/standard-suppliers/${encodeURIComponent(name)}`, { params: { force: true } });
      }
      setSuppliers(res.data?.suppliers || suppliers.filter((s) => s !== name));
      toast.success(`Removed ${name}`);
    } catch { toast.error("Remove failed"); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="standard-suppliers-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <BarChart3 size={18} /> Standard Suppliers
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        The canonical supplier names invoices are normalised to on upload. Add a new supplier here (no code change needed), or remove one you no longer use — a supplier still tied to invoices, price files or mappings will ask for confirmation first.
      </p>
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", maxWidth: 420 }}>
        <input className="form-input" placeholder="New supplier name" value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} data-testid="std-supplier-input" />
        <Button className="btn btn-primary" onClick={add} disabled={busy} data-testid="std-supplier-add">
          <Plus size={15} style={{ marginRight: "0.4rem" }} /> Add
        </Button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
        {suppliers.map((s) => (
          <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", padding: "0.25rem 0.6rem", borderRadius: "999px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", fontSize: "0.82rem", color: "var(--octopus-ice)" }}>
            {s}
            <button onClick={() => remove(s)} title={`Remove ${s}`} data-testid={`std-supplier-del-${s}`}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--octopus-voltage)", opacity: 0.7, display: "flex", padding: 0 }}>
              <Trash2 size={13} />
            </button>
          </span>
        ))}
      </div>
      <p style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.5, marginTop: "0.75rem" }}>
        {suppliers.length} suppliers · changes take effect on the next invoice upload.
      </p>
    </div>
  );
}

function DatabaseExportCard() {
  const [busy, setBusy] = useState(null);     // which collection is currently exporting
  const [progress, setProgress] = useState(0); // records fetched so far

  const COLLECTIONS = [
    { key: "price_files", label: "Price Files" },
    { key: "invoices", label: "Invoices" },
    { key: "price_history", label: "Price History" },
  ];

  // Pull the collection in keyset-paginated pages and stitch them into one
  // JSON file. Many short requests = no single long stream for a gateway or
  // Mongo cursor to cut off midway (which truncated the old one-shot export).
  const exportCollection = async (key, label) => {
    if (busy) return;
    setBusy(key);
    setProgress(0);
    try {
      let after = null;
      let guard = 0;
      const all = [];
      while (true) {
        const url = `${API}/admin/export/${key}?limit=20000` +
          (after ? `&after=${encodeURIComponent(after)}` : "");
        const res = await fetch(url, { method: "GET" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const page = await res.json();
        const docs = Array.isArray(page.docs) ? page.docs : [];
        all.push(...docs);
        setProgress(all.length);
        if (page.done || !page.next_after || docs.length === 0) break;
        if (page.next_after === after) break;  // safety: cursor not advancing
        after = page.next_after;
        if (++guard > 2000) break;             // hard stop (~40M docs)
      }
      const blob = new Blob([JSON.stringify(all)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dateStr = new Date().toISOString().split("T")[0];
      a.href = url;
      a.download = `${key}_${dateStr}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} exported — ${all.length.toLocaleString()} records`);
    } catch (e) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setBusy(null);
      setProgress(0);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="db-export-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Database size={18} /> Database Export (raw)
      </h2>
      <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "1rem" }}>
        Download the raw contents of each collection as an individual JSON file — pure documents exactly as stored, no matching or calculations. For auditing the database at any point.
      </p>
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        {COLLECTIONS.map((c) => (
          <Button
            key={c.key}
            className="btn btn-secondary"
            data-testid={`export-${c.key}-btn`}
            onClick={() => exportCollection(c.key, c.label)}
            disabled={busy !== null}
          >
            {busy === c.key
              ? <Loader2 size={15} style={{ animation: "spin 0.8s linear infinite", marginRight: "0.4rem" }} />
              : <Download size={15} style={{ marginRight: "0.4rem" }} />}
            {busy === c.key
              ? `${c.label}… ${progress.toLocaleString()} rows`
              : `${c.label} (.json)`}
          </Button>
        ))}
      </div>
      <p style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.5, marginTop: "0.75rem" }}>
        Invoices is a large collection — that download may take a little while to prepare.
      </p>
    </div>
  );
}

function ExclusionCard() {
  const [enabled, setEnabled] = useState(true);
  const [words, setWords] = useState([]);
  const [newWord, setNewWord] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    apiGet("/exclusion-settings").then(d => {
      setEnabled(d?.enabled ?? true);
      setWords(Array.isArray(d?.words) ? d.words : []);
    }).finally(() => setLoading(false));
  }, []);

  const addWord = () => {
    const w = newWord.trim();
    if (!w) return;
    if (words.some(x => x.toLowerCase() === w.toLowerCase())) { setNewWord(""); return; }
    setWords(prev => [...prev, w].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
    setNewWord("");
    setDirty(true);
  };
  const removeWord = (w) => { setWords(prev => prev.filter(x => x !== w)); setDirty(true); };
  const toggle = () => { setEnabled(e => !e); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/exclusion-settings`, { enabled, words });
      toast.success("Exclusion settings saved — dashboards will recompute");
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="exclusion-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
        Transport / freight exclusion
      </h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem", opacity: 0.85, maxWidth: "700px" }}>
        Lines whose description contains any of these words (whole-word, case-insensitive) are excluded
        from all dashboards and reports while the toggle is on. The data isn't deleted — it stays in the
        invoice records and on the Invoices page; it's just left out of the analytics.
      </p>

      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <>
          <label style={{ display: "inline-flex", alignItems: "center", gap: "0.6rem", cursor: "pointer", marginBottom: "1rem" }} data-testid="exclusion-toggle">
            <span
              onClick={toggle}
              style={{
                width: "42px", height: "24px", borderRadius: "999px", flexShrink: 0, position: "relative",
                background: enabled ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.12)",
                border: `1px solid ${enabled ? "rgba(34,197,94,0.7)" : "rgba(255,255,255,0.2)"}`,
                transition: "background 0.15s",
              }}
            >
              <span style={{
                position: "absolute", top: "2px", left: enabled ? "20px" : "2px",
                width: "18px", height: "18px", borderRadius: "50%", background: enabled ? "#22c55e" : "#9ca3af",
                transition: "left 0.15s",
              }} />
            </span>
            <span style={{ color: "var(--octopus-ice)", fontSize: "0.9rem", fontWeight: 600 }}>
              {enabled ? "Exclusion ON — transport/freight hidden from analytics" : "Exclusion OFF — everything included"}
            </span>
          </label>

          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", maxWidth: "420px" }}>
            <input
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addWord(); } }}
              placeholder="e.g. CARRIAGE"
              className="form-input"
              data-testid="exclusion-word-input"
              style={{ flex: 1 }}
            />
            <Button onClick={addWord} className="btn btn-secondary" data-testid="exclusion-add-word">
              <Plus size={14} style={{ marginRight: "0.25rem" }} /> Add word
            </Button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
            {words.length === 0 ? (
              <span style={{ color: "var(--octopus-voltage)", opacity: 0.6, fontSize: "0.85rem" }}>No words yet — nothing is excluded.</span>
            ) : words.map((w) => (
              <span key={w} data-testid={`exclusion-word-${w}`} style={{
                display: "inline-flex", alignItems: "center", gap: "0.4rem", padding: "0.25rem 0.6rem",
                borderRadius: "999px", background: "rgba(255,216,79,0.12)", border: "1px solid rgba(255,216,79,0.35)",
                color: "#FFD84F", fontSize: "0.8rem", fontWeight: 600, fontFamily: "monospace",
              }}>
                {w}
                <button onClick={() => removeWord(w)} title={`Remove ${w}`}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--octopus-pink)", padding: 0, display: "inline-flex" }}>
                  <Trash2 size={13} />
                </button>
              </span>
            ))}
          </div>

          <Button onClick={save} disabled={saving || !dirty} className="btn btn-primary" data-testid="exclusion-save">
            {saving ? "Saving…" : dirty ? "Save exclusion settings" : "Saved"}
          </Button>
        </>
      )}
    </div>
  );
}


/**
 * Market price index (DBT building-materials benchmark) used as the overlay
 * line on the Inflation page. Manually maintained: download the latest monthly
 * figures from the DBT source (link provided), then upload a 2-column CSV
 * (month, index value). Uploads merge by month, so you just add new months.
 */
function MarketIndexCard() {
  const [info, setInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    apiGet("/market-index").then(setInfo).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/market-index`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = res.data?.updated || [];
      toast.success(`Updated ${updated.length} index${updated.length !== 1 ? "es" : ""}: ${updated.join(", ")}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteOne = async (name) => {
    if (!window.confirm(`Delete the "${name}" index? It will disappear from the Inflation page.`)) return;
    try {
      await axios.delete(`${API}/market-index`, { params: { name } });
      toast.success(`Deleted "${name}"`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Delete ALL market indexes? The Inflation page will show your data only.")) return;
    try {
      await axios.delete(`${API}/market-index`);
      toast.success("All market indexes cleared");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clear failed");
    }
  };

  const sourceUrl = info?.source_url || "https://www.gov.uk/government/collections/building-materials-and-components-monthly-statistics-2012";
  const indices = info?.indices || [];

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="market-index-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <BarChart3 size={18} /> Market price indexes (Inflation overlay)
      </h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem", opacity: 0.85, maxWidth: "700px" }}>
        Adds market benchmark lines to the Inflation page so you can compare specific suppliers against
        specific indexes. Easiest: download the DBT building-materials statistics workbook and upload the
        <strong>.xlsx as-is</strong> — the monthly materials table (Table 2) is detected automatically and every
        material becomes a named index, no editing needed. A CSV also works (month in column A, one column per
        index, header = name). One upload can add several indexes at once; uploads merge by month.
      </p>

      <a href={sourceUrl} target="_blank" rel="noopener noreferrer"
        data-testid="market-index-source-link"
        style={{ display: "inline-block", fontSize: "0.85rem", color: "var(--octopus-ice)", textDecoration: "underline", marginBottom: "1rem" }}>
        Open DBT building-materials statistics (source) ↗
      </a>

      <div data-testid="market-index-status" style={{
        padding: "0.65rem 0.9rem", borderRadius: "6px", margin: "0.5rem 0 1rem", fontSize: "0.85rem",
        background: indices.length ? "rgba(34,197,94,0.07)" : "rgba(255,216,79,0.07)",
        border: `1px solid ${indices.length ? "rgba(34,197,94,0.3)" : "rgba(255,216,79,0.35)"}`,
        color: "var(--octopus-ice)",
      }}>
        {info == null ? "Loading…" : indices.length ? (
          <div style={{ display: "grid", gap: "0.4rem" }}>
            {indices.map((ix) => (
              <div key={ix.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
                <span><strong>{ix.name}</strong> <span style={{ opacity: 0.7 }}>· {ix.count} months ({ix.first_month} → {ix.last_month})</span></span>
                <button onClick={() => deleteOne(ix.name)} title={`Delete ${ix.name}`} data-testid={`market-index-delete-${ix.name}`}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--octopus-pink)", padding: "0.15rem", flexShrink: 0 }}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <>No market indexes uploaded — the Inflation page shows your data only, with no benchmark lines.</>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} style={{ display: "none" }} data-testid="market-index-file-input" />
      <div style={{ display: "flex", gap: "0.75rem" }}>
        <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="btn btn-primary" data-testid="market-index-upload-btn">
          <Upload size={14} style={{ marginRight: "0.35rem" }} />
          {uploading ? "Uploading…" : indices.length ? "Add / update indexes (CSV)" : "Upload indexes (CSV)"}
        </Button>
        {indices.length > 0 && (
          <Button onClick={handleClearAll} className="btn btn-danger" data-testid="market-index-clear-btn">
            <Trash2 size={14} style={{ marginRight: "0.35rem" }} /> Clear all
          </Button>
        )}
      </div>
    </div>
  );
}


/**
 * Per-unit overcharge threshold (£). A positive (invoice − price-file)
 * difference smaller than this is NOT classified as an overcharge — across
 * the dashboard, supplier metrics, top overcharges, trends, reports CSV
 * download and supplier deep-dive. 0.00 = original "every positive
 * difference counts" behaviour.
 *
 * Increments of £0.01 (a penny). Changing this invalidates the analytics
 * cache server-side so the dashboard recomputes on next load.
 */
function PriceDriftAlertCard() {
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const [driftPct, setDriftPct] = useState(10);
  const [maxPct, setMaxPct] = useState(60);
  const [minLines, setMinLines] = useState(3);
  const [lookback, setLookback] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    apiGet("/settings/price-drift-alert").then(d => {
      if (d) { setEnabled(!!d.enabled); setEmail(d.fallback_email || ""); setDriftPct(Number(d.drift_pct) || 10); setMaxPct(Number(d.max_pct) || 60); setMinLines(Number(d.min_lines) || 3); setLookback(Number(d.lookback_months) || 3); }
    }).finally(() => setLoading(false));
  }, []);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const payload = () => ({ enabled, drift_pct: Number(driftPct) || 10, max_pct: Number(maxPct) || 60, min_lines: Number(minLines) || 3, lookback_months: Number(lookback) || 3, fallback_email: email.trim() });

  const handleSave = async () => {
    if (email.trim() && !validEmail) { toast.error("Enter a valid fallback email"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/price-drift-alert`, payload());
      setDriftPct(Number(res.data?.drift_pct) || 10);
      toast.success(enabled ? `On — weekly Monday alert when invoiced prices drift over ${res.data?.drift_pct}% from the file.` : "Saved (currently off).");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!email.trim() || !validEmail) { toast.error("Add a valid email to send the test to"); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/price-drift-alert/test`, payload());
      toast.success(res.data?.sample ? `Sample sent to ${res.data?.to}.` : `Test sent to ${res.data?.to} — ${res.data?.drifts} drift(s).`);
    } catch (err) { toast.error(err.response?.data?.detail || "Test send failed — check RESEND_API_KEY / sender config."); }
    finally { setTesting(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="price-drift-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Price Drift Alert</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.85, maxWidth: "660px" }}>
        A weekly Monday email to the owning Procurement Manager when a product's recent invoiced price has drifted from the price on file, in either direction, by between the "drift over" and "ignore over" percentages. That upper cap skips huge gaps that are almost always data errors rather than real price changes. Only fires when the drift shows across at least the "min lines" recent invoices, so a one-off keying error won't trigger it.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", cursor: "pointer" }} onClick={() => setEnabled(v => !v)}>
            <span role="switch" aria-checked={enabled} style={{ width: 42, height: 24, borderRadius: 999, flexShrink: 0, position: "relative", background: enabled ? "var(--color-primary-500, #ec4899)" : "rgba(255,255,255,0.15)", transition: "background 0.15s ease" }}>
              <span style={{ position: "absolute", top: 3, left: enabled ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s ease" }} />
            </span>
            <span style={{ fontWeight: 600, color: "var(--octopus-ice)" }}>{enabled ? "Enabled" : "Disabled"}</span>
          </div>
          <div className="form-group" style={{ maxWidth: 400 }}>
            <label className="form-label" htmlFor="pd-email">Fallback email</label>
            <input id="pd-email" type="email" className="form-input" placeholder="name@octopusenergy.services" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pd-email-input" />
            {email.trim() && !validEmail && <p style={{ fontSize: "0.72rem", color: "var(--octopus-pink, #fb7185)", marginTop: "0.25rem" }}>Doesn't look like a valid email.</p>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "0.9rem", maxWidth: 640, marginBottom: "1.25rem" }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="pd-pct">Drift over %</label>
              <input id="pd-pct" type="number" min="0.5" step="0.5" className="form-input" value={driftPct} onChange={(e) => setDriftPct(e.target.value)} data-testid="pd-pct-input" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="pd-max">Ignore over %</label>
              <input id="pd-max" type="number" min="1" step="1" className="form-input" value={maxPct} onChange={(e) => setMaxPct(e.target.value)} data-testid="pd-max-input" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="pd-lines">Min lines</label>
              <input id="pd-lines" type="number" min="1" max="50" className="form-input" value={minLines} onChange={(e) => setMinLines(e.target.value)} data-testid="pd-lines-input" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="pd-lookback">Look back (m)</label>
              <input id="pd-lookback" type="number" min="1" max="24" className="form-input" value={lookback} onChange={(e) => setLookback(e.target.value)} data-testid="pd-lookback-input" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.6rem" }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button className="btn btn-secondary" onClick={handleTest} disabled={testing}>{testing ? "Sending…" : "Send test to me"}</button>
          </div>
        </>
      )}
    </div>
  );
}

function ManagerDigestCard() {
  const [enabled, setEnabled] = useState(false);
  const [cadence, setCadence] = useState("monthly");
  const [recipients, setRecipients] = useState("");   // one address per line / comma-separated
  const [staleMonths, setStaleMonths] = useState(3);
  const [testEmail, setTestEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    apiGet("/settings/manager-digest").then(d => {
      if (d) {
        setEnabled(!!d.enabled);
        setCadence(d.cadence || "monthly");
        setRecipients((d.recipients || []).join("\n"));
        setStaleMonths(Number(d.stale_months) || 3);
      }
    }).finally(() => setLoading(false));
  }, []);

  const parsedRecipients = recipients.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const badRecipients = parsedRecipients.filter(e => !emailRe.test(e));
  const testValid = !testEmail.trim() || emailRe.test(testEmail.trim());

  const payload = () => ({ enabled, cadence, recipients: parsedRecipients, stale_months: Number(staleMonths) || 3 });
  const cadenceWord = { daily: "every weekday morning", weekly: "every Monday", monthly: "on the 1st of each month" }[cadence] || "weekly";

  const handleSave = async () => {
    if (badRecipients.length) { toast.error(`Not a valid email: ${badRecipients[0]}`); return; }
    if (enabled && !parsedRecipients.length) { toast.error("Add at least one recipient to enable the digest"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/manager-digest`, payload());
      const n = (res.data?.recipients || []).length;
      toast.success(enabled ? `On — digest sends ${cadenceWord} to ${n} recipient${n === 1 ? "" : "s"}.` : "Saved (currently off).");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    const to = testEmail.trim();
    if (!to || !testValid) { toast.error("Add a valid email to send the test to"); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/manager-digest/test`, { ...payload(), test_email: to });
      toast.success(res.data?.sample ? `Sample digest sent to ${res.data?.to}.` : `Test digest sent to ${res.data?.to}.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Test send failed — check RESEND_API_KEY / sender config."); }
    finally { setTesting(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="manager-digest-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Manager Digest</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.85, maxWidth: "660px" }}>
        A recurring summary email to the procurement managers, pulling together what needs attention (uncaptured price reductions, price files going stale, lines flagged as likely data issues), the submissions waiting for approval with their modelled annual impact, and the running impact of submissions already approved. Nothing to report in a period means no email that period.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", cursor: "pointer" }} onClick={() => setEnabled(v => !v)}>
            <span role="switch" aria-checked={enabled} style={{ width: 42, height: 24, borderRadius: 999, flexShrink: 0, position: "relative", background: enabled ? "var(--color-primary-500, #ec4899)" : "rgba(255,255,255,0.15)", transition: "background 0.15s ease" }}>
              <span style={{ position: "absolute", top: 3, left: enabled ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s ease" }} />
            </span>
            <span style={{ fontWeight: 600, color: "var(--octopus-ice)" }}>{enabled ? "Enabled" : "Disabled"}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: "0.9rem", maxWidth: 640, marginBottom: "1.25rem" }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="md-cadence">Cadence</label>
              <select id="md-cadence" className="form-input" value={cadence} onChange={(e) => setCadence(e.target.value)} data-testid="md-cadence-input">
                <option value="daily">Daily (weekday morning)</option>
                <option value="weekly">Weekly (Monday)</option>
                <option value="monthly">Monthly (1st)</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="md-stale">Stale after (months)</label>
              <input id="md-stale" type="number" min="1" max="24" className="form-input" value={staleMonths} onChange={(e) => setStaleMonths(e.target.value)} data-testid="md-stale-input" />
            </div>
          </div>
          <div className="form-group" style={{ maxWidth: 480 }}>
            <label className="form-label" htmlFor="md-recipients">Recipients (one per line)</label>
            <textarea id="md-recipients" className="form-input" rows={4} placeholder={"alice@octopusenergy.services\nbob@octopusenergy.services"} value={recipients} onChange={(e) => setRecipients(e.target.value)} data-testid="md-recipients-input" style={{ resize: "vertical", fontFamily: "inherit" }} />
            {badRecipients.length > 0 && <p style={{ fontSize: "0.72rem", color: "var(--octopus-pink, #fb7185)", marginTop: "0.25rem" }}>Not a valid email: {badRecipients[0]}</p>}
            {parsedRecipients.length > 0 && badRecipients.length === 0 && <p style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", marginTop: "0.25rem", opacity: 0.8 }}>{parsedRecipients.length} recipient{parsedRecipients.length === 1 ? "" : "s"}.</p>}
          </div>
          <div className="form-group" style={{ maxWidth: 400 }}>
            <label className="form-label" htmlFor="md-test">Send test to</label>
            <input id="md-test" type="email" className="form-input" placeholder="name@octopusenergy.services" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="md-test-input" />
            {testEmail.trim() && !testValid && <p style={{ fontSize: "0.72rem", color: "var(--octopus-pink, #fb7185)", marginTop: "0.25rem" }}>Doesn't look like a valid email.</p>}
          </div>
          <div style={{ display: "flex", gap: "0.6rem" }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button className="btn btn-secondary" onClick={handleTest} disabled={testing}>{testing ? "Sending…" : "Send test"}</button>
          </div>
        </>
      )}
    </div>
  );
}

function BoardPackCard() {
  const [enabled, setEnabled] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    apiGet("/settings/board-pack").then(d => {
      if (d) { setEnabled(!!d.enabled); setRecipient(d.recipient || ""); }
    }).finally(() => setLoading(false));
  }, []);

  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const recipientValid = !recipient.trim() || emailRe.test(recipient.trim());
  const testValid = !testEmail.trim() || emailRe.test(testEmail.trim());

  const handleSave = async () => {
    if (recipient.trim() && !recipientValid) { toast.error("Enter a valid recipient email"); return; }
    if (enabled && !recipient.trim()) { toast.error("Add a recipient to enable the board pack"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/board-pack`, { enabled, recipient: recipient.trim() });
      toast.success(res.data?.enabled ? "On — monthly nudge with the board-pack figures." : "Saved (currently off).");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    const to = testEmail.trim() || recipient.trim();
    if (!to || !emailRe.test(to)) { toast.error("Add a valid email to send the test to"); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/board-pack/test`, { enabled, recipient: recipient.trim(), test_email: to });
      toast.success(res.data?.sample ? `Sample sent to ${res.data?.to}.` : `Test sent to ${res.data?.to}.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Test send failed — check RESEND_API_KEY / sender config."); }
    finally { setTesting(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="board-pack-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Board Pack</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.85, maxWidth: "660px" }}>
        A monthly nudge with the director-level wrap-up figures (savings and recovery position, cost-of-install trend and month-on-month move) sent to you, ready to drop into the Octopus board deck for the SLT. It does not send to the SLT itself and does not build the deck, it hands you the numbers so you can finish and present it.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", cursor: "pointer" }} onClick={() => setEnabled(v => !v)}>
            <span role="switch" aria-checked={enabled} style={{ width: 42, height: 24, borderRadius: 999, flexShrink: 0, position: "relative", background: enabled ? "var(--color-primary-500, #ec4899)" : "rgba(255,255,255,0.15)", transition: "background 0.15s ease" }}>
              <span style={{ position: "absolute", top: 3, left: enabled ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s ease" }} />
            </span>
            <span style={{ fontWeight: 600, color: "var(--octopus-ice)" }}>{enabled ? "Enabled" : "Disabled"}</span>
          </div>
          <div className="form-group" style={{ maxWidth: 400 }}>
            <label className="form-label" htmlFor="bp-recipient">Recipient (you)</label>
            <input id="bp-recipient" type="email" className="form-input" placeholder="name@octopusenergy.services" value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="bp-recipient-input" />
            {recipient.trim() && !recipientValid && <p style={{ fontSize: "0.72rem", color: "var(--octopus-pink, #fb7185)", marginTop: "0.25rem" }}>Doesn't look like a valid email.</p>}
          </div>
          <div className="form-group" style={{ maxWidth: 400 }}>
            <label className="form-label" htmlFor="bp-test">Send test to</label>
            <input id="bp-test" type="email" className="form-input" placeholder="defaults to the recipient above" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="bp-test-input" />
            {testEmail.trim() && !testValid && <p style={{ fontSize: "0.72rem", color: "var(--octopus-pink, #fb7185)", marginTop: "0.25rem" }}>Doesn't look like a valid email.</p>}
          </div>
          <div style={{ display: "flex", gap: "0.6rem" }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button className="btn btn-secondary" onClick={handleTest} disabled={testing}>{testing ? "Sending…" : "Send test"}</button>
          </div>
        </>
      )}
    </div>
  );
}

function SupplierConfirmationCard() {
  const [enabled, setEnabled] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    apiGet("/settings/supplier-confirmation").then(d => {
      if (d) setEnabled(!!d.enabled);
    }).finally(() => setLoading(false));
  }, []);

  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const testValid = !testEmail.trim() || emailRe.test(testEmail.trim());

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/supplier-confirmation`, { enabled });
      toast.success(res.data?.enabled ? "On — suppliers are emailed when their price update is approved." : "Saved (currently off).");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    const to = testEmail.trim();
    if (!to || !testValid) { toast.error("Add a valid email to send the test to"); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/supplier-confirmation/test`, { enabled, test_email: to });
      toast.success(`Sample confirmation sent to ${res.data?.to}.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Test send failed — check RESEND_API_KEY / sender config."); }
    finally { setTesting(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="supplier-confirmation-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Supplier Confirmation</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.85, maxWidth: "660px" }}>
        When a price submission is approved, email the submitting supplier a short confirmation (reference, number of lines, effective dates). It goes to the contact email on that supplier's record, fires on every approval route, and never fires on a rejection. Suppliers with no contact email on file are skipped.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", cursor: "pointer" }} onClick={() => setEnabled(v => !v)}>
            <span role="switch" aria-checked={enabled} style={{ width: 42, height: 24, borderRadius: 999, flexShrink: 0, position: "relative", background: enabled ? "var(--color-primary-500, #ec4899)" : "rgba(255,255,255,0.15)", transition: "background 0.15s ease" }}>
              <span style={{ position: "absolute", top: 3, left: enabled ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s ease" }} />
            </span>
            <span style={{ fontWeight: 600, color: "var(--octopus-ice)" }}>{enabled ? "Enabled" : "Disabled"}</span>
          </div>
          <div className="form-group" style={{ maxWidth: 400 }}>
            <label className="form-label" htmlFor="sc-test">Send test to</label>
            <input id="sc-test" type="email" className="form-input" placeholder="name@octopusenergy.services" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="sc-test-input" />
            {testEmail.trim() && !testValid && <p style={{ fontSize: "0.72rem", color: "var(--octopus-pink, #fb7185)", marginTop: "0.25rem" }}>Doesn't look like a valid email.</p>}
            <p style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", marginTop: "0.35rem", opacity: 0.8 }}>Sends a sample confirmation so you can see what suppliers receive.</p>
          </div>
          <div style={{ display: "flex", gap: "0.6rem" }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button className="btn btn-secondary" onClick={handleTest} disabled={testing}>{testing ? "Sending…" : "Send test"}</button>
          </div>
        </>
      )}
    </div>
  );
}

// At-a-glance health of every background integration.
function SystemStatusCard() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); apiGet("/settings/integration-status").then(d => setItems(d?.integrations || [])).catch(() => setItems([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const rel = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso); const s = (Date.now() - d.getTime()) / 1000;
    if (isNaN(s)) return iso;
    if (s < 90) return "just now";
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  };
  const dot = { ok: "#34d399", error: "#fb7185", warn: "#fbbf24", off: "#6b7280" };
  const label = { ok: "Healthy", error: "Error", warn: "Warning", off: "Not configured" };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="system-status-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>System Status</h2>
        <button className="btn btn-secondary" onClick={load} style={{ padding: "0.25rem 0.7rem", fontSize: "0.8rem" }}>Refresh</button>
      </div>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem", opacity: 0.85, maxWidth: "660px" }}>
        Live health of the background integrations. Green is configured and running; grey is switched off; red needs a look.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <div style={{ border: "1px solid rgba(96,240,248,0.12)", borderRadius: 8, overflow: "hidden" }}>
          {(items || []).map((it, i) => (
            <div key={it.key} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1.6fr 0.9fr", gap: "0.8rem", alignItems: "center", padding: "0.7rem 0.9rem", borderTop: i ? "1px solid rgba(96,240,248,0.08)" : "none" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--octopus-ice)", fontWeight: 600, fontSize: "0.88rem" }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot[it.status] || dot.off, flexShrink: 0 }} />
                {it.name}
                {it.source && <span style={{ fontSize: "0.7rem", color: "var(--octopus-voltage)", opacity: 0.7, border: "1px solid rgba(96,240,248,0.2)", borderRadius: 4, padding: "1px 5px" }}>{it.source}</span>}
              </span>
              <span style={{ fontSize: "0.8rem", color: dot[it.status] || dot.off, fontWeight: 600 }}>{label[it.status] || it.status}</span>
              <span style={{ fontSize: "0.8rem", color: "var(--octopus-voltage)", opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.detail}</span>
              <span style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.7, textAlign: "right" }}>{it.lastRun ? rel(it.lastRun) : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Preview the review-queue impact banner + UoM panel against a synthesized
// submission, so it can be seen without waiting for a real price submission.
function ReviewQueuePreviewCard() {
  const [cpi, setCpi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const g0 = (n) => "£" + Math.round(n || 0).toLocaleString("en-GB");
  const g2 = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const run = async () => {
    setLoading(true); setMsg("");
    try {
      const d = await apiGet("/qbr/submission-impact/sample");
      if (!d?.loaded) { setMsg("Upload the dispatch data in Price Modelling first."); setCpi(null); }
      else if (d.empty || !d.overall) { setMsg("No priced lines available to model."); setCpi(null); }
      else setCpi(d);
    } catch { setMsg("Could not build the preview."); }
    finally { setLoading(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="review-preview-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Review Queue Preview</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem", opacity: 0.85, maxWidth: "660px" }}>
        See how the cost-of-install impact and unit-of-measure warnings appear on a submission in the review queue, using a synthesized price change against your loaded dispatch data — no real submission needed.
      </p>
      <button className="btn btn-primary" onClick={run} disabled={loading}>{loading ? "Building…" : "Preview impact"}</button>
      {msg && <p style={{ fontSize: "0.82rem", color: "var(--octopus-voltage)", opacity: 0.8, marginTop: "0.8rem" }}>{msg}</p>}
      {cpi && (
        <div style={{ marginTop: "1.25rem", padding: "16px", background: "rgba(10,18,42,0.4)", border: "1px solid var(--hairline)", borderRadius: 10 }}>
          <div style={{ fontSize: 12.5, color: "var(--octopus-voltage)", opacity: 0.7, marginBottom: 12 }}>As it appears in the review queue:</div>
          <div style={{ padding: "0.65rem 0.9rem", background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.3)", borderRadius: 6, marginBottom: "1rem", display: "flex", alignItems: "baseline", gap: "0.6rem", flexWrap: "wrap", fontSize: "0.85rem" }}>
            <span style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>Projected cost-of-install impact</span>
            <strong style={{ fontSize: "1.05rem", color: cpi.overall.delta >= 0 ? "#fda4af" : "#86efac" }}>{cpi.overall.delta >= 0 ? "+" : "−"}{g2(Math.abs(cpi.overall.delta))} / install</strong>
            <span style={{ color: "var(--octopus-voltage)" }}>({cpi.overall.deltaPct >= 0 ? "+" : ""}{cpi.overall.deltaPct}% · {g0(cpi.overall.currentCost)} → {g0(cpi.overall.newCost)})</span>
            <span style={{ color: "var(--octopus-voltage)", opacity: 0.6, fontSize: "0.72rem" }}>{cpi.drivers?.length || 0} line(s) affect installs · theoretical — modelled vs dispatch data</span>
          </div>
          {cpi.uomWarnings?.length > 0 && (
            <div style={{ padding: "0.65rem 0.9rem", background: "rgba(245,158,11,0.09)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 6, fontSize: "0.83rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", marginBottom: "0.4rem" }}>
                <AlertTriangle size={14} style={{ color: "#f59e0b" }} />
                <strong style={{ color: "#fbbf24" }}>Possible unit-of-measure mismatch — excluded from the impact above</strong>
              </div>
              {cpi.uomWarnings.slice(0, 6).map((w, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", padding: "0.25rem 0", borderTop: i ? "1px solid rgba(245,158,11,0.15)" : "none", fontSize: "0.78rem" }}>
                  <span style={{ color: "var(--octopus-voltage)" }}><strong style={{ fontFamily: "monospace" }}>{w.part}</strong> {w.description ? `· ${w.description}` : ""}</span>
                  <span style={{ color: "#fbbf24", whiteSpace: "nowrap" }}>submitted {g2(w.submitted)} vs dispatched {g2(w.dispatchUnit)} (~{w.factor}×)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Install-variant rules: which appliance brand a job is, keyed off the unit
// line (exact SKU, SKU prefix, or a description/supplier keyword). Applied on
// the next dispatch upload; drives by-variant modelling.
function VariantMapCard() {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet("/qbr/variant-map").then(d => setVariants(d?.variants || [])).finally(() => setLoading(false));
  }, []);

  const upd = (i, field, val) => setVariants(vs => vs.map((v, j) => j === i ? { ...v, [field]: val } : v));
  const updList = (i, field, val) => upd(i, field, val.split(",").map(s => s.trim()).filter(Boolean));
  const addRow = () => setVariants(vs => [...vs, { name: "", area: "HEATPUMP", skus: [], prefixes: [], keywords: [] }]);
  const removeRow = (i) => setVariants(vs => vs.filter((_, j) => j !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      const clean = variants.filter(v => (v.name || "").trim() && (v.area || "").trim());
      const res = await axios.put(`${API}/qbr/variant-map`, { variants: clean });
      setVariants(res.data?.variants || clean);
      toast.success("Variant map saved. Re-upload the dispatch data in Price Modelling to apply.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save variant map"); }
    finally { setSaving(false); }
  };

  const inp = { background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 6, padding: "5px 8px", fontSize: "0.8rem" };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="variant-map-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Variant Map</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem", opacity: 0.85, maxWidth: "680px" }}>
        Classify each job by its appliance brand from the unit line. A variant matches when a job's install type equals the <strong>area</strong> and a product matches by exact <strong>SKU</strong>, SKU <strong>prefix</strong>, or a description/supplier <strong>keyword</strong>. Comma-separate multiple values. Applied on the next dispatch upload.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: "0.8rem", width: "100%", minWidth: 720 }}>
              <thead>
                <tr>{["Variant", "Area", "SKUs", "Prefixes", "Keywords", ""].map(h => <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: "var(--octopus-voltage)", fontWeight: 600 }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {variants.map((v, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 6px" }}><input value={v.name || ""} onChange={e => upd(i, "name", e.target.value)} style={{ ...inp, width: 90 }} /></td>
                    <td style={{ padding: "4px 6px" }}><input value={v.area || ""} onChange={e => upd(i, "area", e.target.value.toUpperCase())} style={{ ...inp, width: 100 }} /></td>
                    <td style={{ padding: "4px 6px" }}><input value={(v.skus || []).join(", ")} onChange={e => updList(i, "skus", e.target.value)} style={{ ...inp, width: 200 }} placeholder="EDLA123, ..." /></td>
                    <td style={{ padding: "4px 6px" }}><input value={(v.prefixes || []).join(", ")} onChange={e => updList(i, "prefixes", e.target.value)} style={{ ...inp, width: 110 }} placeholder="EDLA" /></td>
                    <td style={{ padding: "4px 6px" }}><input value={(v.keywords || []).join(", ")} onChange={e => updList(i, "keywords", e.target.value)} style={{ ...inp, width: 150 }} placeholder="TESLA, POWERWALL" /></td>
                    <td style={{ padding: "4px 6px" }}><button onClick={() => removeRow(i)} style={{ background: "none", border: "1px solid #2a1c47", borderRadius: 6, color: "var(--octopus-voltage)", cursor: "pointer", padding: "4px 8px" }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: "0.6rem", marginTop: "1rem" }}>
            <button className="btn btn-secondary" onClick={addRow} style={{ padding: "0.3rem 0.8rem", fontSize: "0.82rem" }}>+ Add variant</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save variant map"}</button>
          </div>
        </>
      )}
    </div>
  );
}

// Basic product-code → LCT-type upload. Fills the install type for dispatch
// rows the feed left Unspecified (applied on the next dispatch upload).
function ProductTypeMapCard() {
  const [count, setCount] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => apiGet("/qbr/product-map").then(d => setCount(d?.count ?? 0)).catch(() => setCount(0));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/qbr/product-map/upload`, fd);
      setCount(res.data?.count ?? 0);
      toast.success(`${res.data?.count?.toLocaleString()} products mapped. Re-upload the dispatch data in Price Modelling to apply.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed — check the CSV."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="product-map-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Product Type Map</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem", opacity: 0.85, maxWidth: "660px" }}>
        Upload a simple CSV of <strong>product code</strong> and <strong>LCT type</strong> (two columns). It fills the job type for any dispatch line the feed left Unspecified, so those costs land in the right bucket. Applied the next time you upload the dispatch data in Price Modelling.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: "0.9rem", flexWrap: "wrap" }}>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onUpload} style={{ display: "none" }} data-testid="product-map-file" />
        <button className="btn btn-primary" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? "Uploading…" : "Upload product map"}</button>
        <span style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.85 }}>
          {count == null ? "…" : count > 0 ? `${count.toLocaleString()} products mapped` : "No map uploaded yet"}
        </span>
      </div>
      <p style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.6, marginTop: "0.8rem" }}>
        Example rows: <code>SFX/ISPE2-PE-BLK1, SOLAR</code> — header row optional.
      </p>
    </div>
  );
}

// Monthly install forecast by LCT type, for two scenarios (operational +
// budgeted). Feeds the £/year and projection on the Price Modelling screen,
// where a PM switches between the two.
function InstallForecastCard() {
  const [months, setMonths] = useState([]);
  const [types, setTypes] = useState([]);
  const [maps, setMaps] = useState({ operational: {}, budgeted: {} });
  const [kind, setKind] = useState("operational");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet("/qbr/install-forecast").then(d => {
      if (d) {
        setMonths(d.months || []);
        setTypes(d.types || []);
        setMaps({ operational: d.operational || {}, budgeted: d.budgeted || {} });
      }
    }).finally(() => setLoading(false));
  }, []);

  const cur = maps[kind] || {};
  const setCell = (t, m, v) => {
    const n = v === "" ? "" : Math.max(0, Number(v) || 0);
    setMaps(prev => {
      const next = { ...prev, [kind]: { ...(prev[kind] || {}) } };
      next[kind][t] = { ...(next[kind][t] || {}) };
      if (n === "" || n === 0) delete next[kind][t][m]; else next[kind][t][m] = n;
      return next;
    });
  };
  const rowTotal = (t) => Object.values(cur[t] || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const grand = types.reduce((a, t) => a + rowTotal(t), 0);

  const monthLabel = (ym) => {
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "short" }) + " " + y.slice(2);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/qbr/install-forecast`, { kind, map: cur, months });
      setMaps(prev => ({ ...prev, [kind]: res.data?.map || cur }));
      toast.success(`${kind === "operational" ? "Operational" : "Budgeted"} forecast saved.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save forecast"); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="forecast-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Install Forecast</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem", opacity: 0.85, maxWidth: "660px" }}>
        Forecast installs by month for each job type, up to a full year. Keep an <strong>operational</strong> and a <strong>budgeted</strong> version — the price modeller uses these for its £/year figures and projection, and a PM can switch between them there.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : types.length === 0 ? (
        <p style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.85rem" }}>No install types yet — upload the dispatch data in Price Modelling first.</p>
      ) : (
        <>
          <div style={{ display: "inline-flex", gap: 2, background: "#0f0a1e", border: "1px solid rgba(96,240,248,0.15)", borderRadius: 999, padding: 3, marginBottom: "1rem" }}>
            {[["operational", "Operational"], ["budgeted", "Budgeted"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setKind(k)} style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "6px 18px", fontSize: 13, fontWeight: 600,
                background: kind === k ? "linear-gradient(90deg,#a855f7,#ec4899)" : "transparent", color: kind === k ? "#fff" : "var(--octopus-voltage)" }}>{lbl}</button>
            ))}
          </div>
          <div style={{ overflowX: "auto", border: "1px solid rgba(96,240,248,0.12)", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", fontSize: "0.8rem", minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ position: "sticky", left: 0, background: "#140e28", padding: "8px 10px", textAlign: "left", color: "var(--octopus-voltage)" }}>Type</th>
                  {months.map(m => <th key={m} style={{ padding: "8px 6px", color: "var(--octopus-voltage)", opacity: 0.8, fontWeight: 600, whiteSpace: "nowrap" }}>{monthLabel(m)}</th>)}
                  <th style={{ padding: "8px 10px", color: "var(--octopus-ice)" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {types.map(t => (
                  <tr key={t}>
                    <td style={{ position: "sticky", left: 0, background: "#140e28", padding: "6px 10px", color: "var(--octopus-ice)", fontWeight: 600, whiteSpace: "nowrap" }}>{t}</td>
                    {months.map(m => (
                      <td key={m} style={{ padding: "3px 4px" }}>
                        <input type="number" min="0" value={(cur[t] && cur[t][m] != null) ? cur[t][m] : ""} onChange={(e) => setCell(t, m, e.target.value)}
                          style={{ width: 52, background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 6, padding: "4px 6px", fontSize: "0.8rem", textAlign: "right" }} />
                      </td>
                    ))}
                    <td style={{ padding: "6px 10px", textAlign: "right", color: "var(--octopus-ice)", fontWeight: 700 }}>{rowTotal(t).toLocaleString()}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ position: "sticky", left: 0, background: "#140e28", padding: "6px 10px", color: "var(--octopus-voltage)", fontWeight: 700 }}>All</td>
                  <td colSpan={months.length} />
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#34d399", fontWeight: 700 }}>{grand.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: "0.6rem", marginTop: "1rem" }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : `Save ${kind} forecast`}</button>
          </div>
        </>
      )}
    </div>
  );
}

// Maps the supplier names in the line-level dispatch data to their PriceGuard
// equivalents, so the price modeller and PriceGuard share one supplier identity.
function SupplierMappingCard() {
  const [dispatch, setDispatch] = useState([]);
  const [priceguard, setPriceguard] = useState([]);
  const [mapping, setMapping] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet("/qbr/supplier-mapping").then(d => {
      if (d) {
        setDispatch(d.dispatchSuppliers || []);
        setPriceguard(d.priceguardSuppliers || []);
        setMapping(d.mapping || {});
        setSuggestions(d.suggestions || {});
      }
    }).finally(() => setLoading(false));
  }, []);

  const set = (name, val) => setMapping(m => ({ ...m, [name]: val }));
  const applySuggestions = () => setMapping(m => ({ ...suggestions, ...m }));
  const unmapped = dispatch.filter(s => !(mapping[s] || "").trim()).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(mapping).forEach(([k, v]) => { if ((v || "").trim()) clean[k] = v.trim(); });
      const res = await axios.put(`${API}/qbr/supplier-mapping`, { map: clean });
      setMapping(res.data?.map || clean);
      toast.success("Supplier mapping saved.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save mapping"); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="supplier-map-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Dispatch Supplier Map</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem", opacity: 0.85, maxWidth: "660px" }}>
        Match each supplier in the line-level dispatch data to its PriceGuard name so the price modeller and PriceGuard treat them as the same supplier. Suggested matches are pre-filled where the names line up; confirm or correct, then save. Leave blank for suppliers that aren't in PriceGuard.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : dispatch.length === 0 ? (
        <p style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.85rem" }}>No dispatch data loaded yet — upload the dispatch export in Price Modelling first.</p>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.8rem", color: "var(--octopus-voltage)", opacity: 0.8 }}>{dispatch.length} dispatch suppliers · <strong style={{ color: unmapped ? "#fbbf24" : "#86efac" }}>{unmapped}</strong> unmapped</span>
            {Object.keys(suggestions).length > 0 && (
              <button className="btn btn-secondary" style={{ padding: "0.25rem 0.7rem", fontSize: "0.78rem" }} onClick={applySuggestions}>Apply {Object.keys(suggestions).length} suggestion(s)</button>
            )}
          </div>
          <div style={{ maxHeight: 460, overflowY: "auto", border: "1px solid rgba(96,240,248,0.12)", borderRadius: 8 }}>
            {dispatch.map((name, i) => (
              <div key={name} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "0.8rem", alignItems: "center", padding: "0.5rem 0.8rem", borderTop: i ? "1px solid rgba(96,240,248,0.08)" : "none" }}>
                <span style={{ fontSize: "0.85rem", color: "var(--octopus-ice)", fontWeight: 500 }}>{name}
                  {!mapping[name] && suggestions[name] && <span style={{ fontSize: "0.72rem", color: "#fbbf24", marginLeft: 6 }}>→ {suggestions[name]}?</span>}
                </span>
                <select className="form-input" style={{ fontSize: "0.82rem", padding: "0.35rem 0.5rem" }} value={mapping[name] || ""} onChange={(e) => set(name, e.target.value)} data-testid={`map-${name}`}>
                  <option value="">— not in PriceGuard —</option>
                  {priceguard.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: "0.6rem", marginTop: "1rem" }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save mapping"}</button>
          </div>
        </>
      )}
    </div>
  );
}

function PriceExpiryReminderCard() {
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const [staleMonths, setStaleMonths] = useState(3);
  const [purchaseMonths, setPurchaseMonths] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    apiGet("/settings/price-expiry-reminder").then(d => {
      if (d) { setEnabled(!!d.enabled); setEmail(d.fallback_email || ""); setStaleMonths(Number(d.stale_months) || 3); setPurchaseMonths(Number(d.purchase_months) || 3); }
    }).finally(() => setLoading(false));
  }, []);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSave = async () => {
    if (email.trim() && !validEmail) { toast.error("Enter a valid fallback email"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/price-expiry-reminder`, { enabled, stale_months: Number(staleMonths) || 3, purchase_months: Number(purchaseMonths) || 3, fallback_email: email.trim() });
      setStaleMonths(Number(res.data?.stale_months) || 3);
      toast.success(enabled ? `On — PMs alerted when a supplier's latest price file is over ${res.data?.stale_months} months old.` : "Saved (currently off).");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!email.trim() || !validEmail) { toast.error("Add a valid email to send the test to"); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/price-expiry-reminder/test`, { enabled, stale_months: Number(staleMonths) || 3, purchase_months: Number(purchaseMonths) || 3, fallback_email: email.trim() });
      toast.success(res.data?.sample ? `Sample sent to ${res.data?.to}.` : `Test sent to ${res.data?.to} — ${res.data?.suppliers} supplier(s) expiring.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Test send failed — check RESEND_API_KEY / sender config."); }
    finally { setTesting(false); }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="price-expiry-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>Stale Price Reminder</h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.85, maxWidth: "640px" }}>
        Emails the owning Procurement Manager (routed by supplier category) when a supplier they manage hasn't submitted a new price file within the "stale after" window <b>and</b> we've still bought products from them within the "purchased within" window — so it only flags prices that actually matter, not unused catalogue lines. Sends at most once per month.
      </p>
      {loading ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", cursor: "pointer" }} onClick={() => setEnabled(v => !v)}>
            <span role="switch" aria-checked={enabled} style={{ width: 42, height: 24, borderRadius: 999, flexShrink: 0, position: "relative", background: enabled ? "var(--color-primary-500, #ec4899)" : "rgba(255,255,255,0.15)", transition: "background 0.15s ease" }}>
              <span style={{ position: "absolute", top: 3, left: enabled ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s ease" }} />
            </span>
            <span style={{ fontWeight: 600, color: "var(--octopus-ice)" }}>{enabled ? "Enabled" : "Disabled"}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 150px 170px", gap: "1rem", maxWidth: 640, marginBottom: "1.25rem" }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="pe-email">Fallback email</label>
              <input id="pe-email" type="email" className="form-input" placeholder="name@octopusenergy.services" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pe-email-input" />
              {email.trim() && !validEmail && <p style={{ fontSize: "0.72rem", color: "var(--octopus-pink, #fb7185)", marginTop: "0.25rem" }}>Doesn't look like a valid email.</p>}
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="pe-stale">Stale after (months)</label>
              <input id="pe-stale" type="number" min="1" max="24" className="form-input" value={staleMonths} onChange={(e) => setStaleMonths(e.target.value)} data-testid="pe-stale-input" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="pe-purchase">Purchased within (months)</label>
              <input id="pe-purchase" type="number" min="1" max="24" className="form-input" value={purchaseMonths} onChange={(e) => setPurchaseMonths(e.target.value)} data-testid="pe-purchase-input" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.6rem" }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button className="btn btn-secondary" onClick={handleTest} disabled={testing}>{testing ? "Sending…" : "Send test to me"}</button>
          </div>
          <p style={{ color: "var(--octopus-voltage)", fontSize: "0.78rem", marginTop: "0.8rem", opacity: 0.7 }}>
            The fallback address receives alerts for any supplier whose category has no owner set. The test sends a digest (or a sample if nothing's expiring) to the fallback address above.
          </p>
        </>
      )}
    </div>
  );
}

function InvoiceReminderCard() {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const [day, setDay] = useState(12);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    apiGet("/settings/invoice-reminder").then(d => {
      if (d) {
        setCfg(d);
        setEnabled(!!d.enabled);
        setEmail(d.recipient_email || "");
        setDay(Number(d.day_of_month) || 12);
      }
    }).finally(() => setLoading(false));
  }, []);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSave = async () => {
    if (email.trim() && !validEmail) {
      toast.error("Enter a valid recipient email address");
      return;
    }
    if (enabled && !email.trim()) {
      toast.error("Add a recipient email before enabling the reminder");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/invoice-reminder`, {
        enabled,
        recipient_email: email.trim(),
        day_of_month: Number(day) || 12,
      });
      setCfg(res.data);
      setDay(Number(res.data?.day_of_month) || 12);
      toast.success(enabled
        ? `Reminder on — sends on day ${res.data?.day_of_month} each month to ${res.data?.recipient_email}.`
        : "Reminder saved (currently off).");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!email.trim() || !validEmail) {
      toast.error("Add a valid recipient email first");
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/invoice-reminder/test`, {
        enabled,
        recipient_email: email.trim(),
        day_of_month: Number(day) || 12,
      });
      const n = res.data?.missing_count ?? 0;
      toast.success(`Test sent to ${res.data?.to} — ${n} supplier${n === 1 ? "" : "s"} missing for ${res.data?.target_month}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed — check RESEND_API_KEY / sender config.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="invoice-reminder-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
        Invoice Reminder
      </h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.85, maxWidth: "640px" }}>
        Emails a monthly nudge to upload last month's invoices, listing any suppliers that usually
        invoice but have nothing for the target month. Sends once, on or after the chosen day — a
        restart makes it fire late rather than twice.
      </p>

      {loading ? (
        <p style={{ opacity: 0.6 }}>Loading…</p>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", cursor: "pointer" }} onClick={() => setEnabled(v => !v)}>
            <span
              role="switch"
              aria-checked={enabled}
              style={{
                width: 42, height: 24, borderRadius: 999, flexShrink: 0, position: "relative",
                background: enabled ? "var(--color-primary-500, #ec4899)" : "rgba(255,255,255,0.15)",
                transition: "background 0.15s ease",
              }}
            >
              <span style={{
                position: "absolute", top: 3, left: enabled ? 21 : 3, width: 18, height: 18,
                borderRadius: "50%", background: "#fff", transition: "left 0.15s ease",
              }} />
            </span>
            <span style={{ fontWeight: 600, color: "var(--octopus-ice)" }}>
              {enabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: "1rem", maxWidth: 560, marginBottom: "1.25rem" }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="reminder-email">Recipient email</label>
              <input
                id="reminder-email"
                type="email"
                className="form-input"
                placeholder="name@octopusenergy.services"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="reminder-email-input"
              />
              {email.trim() && !validEmail && (
                <p style={{ fontSize: "0.72rem", color: "var(--octopus-pink, #fb7185)", marginTop: "0.25rem" }}>
                  Doesn't look like a valid email.
                </p>
              )}
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="reminder-day">Day of month</label>
              <input
                id="reminder-day"
                type="number"
                min="1"
                max="28"
                className="form-input"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                data-testid="reminder-day-input"
              />
              <p style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", marginTop: "0.25rem", opacity: 0.6 }}>
                1–28
              </p>
            </div>
          </div>

          {cfg?.last_sent_month && (
            <p style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.7, marginBottom: "1rem" }}>
              Last sent for: {cfg.last_sent_month}
            </p>
          )}

          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary"
              data-testid="reminder-save-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin" style={{ marginRight: "0.4rem" }} /> : null}
              Save
            </Button>
            <Button
              onClick={handleTest}
              disabled={testing || !email.trim() || !validEmail}
              className="btn"
              style={{ background: "transparent", border: "1px solid var(--hairline, rgba(255,255,255,0.15))", color: "var(--octopus-ice)" }}
              data-testid="reminder-test-btn"
            >
              {testing ? <Loader2 size={16} className="animate-spin" style={{ marginRight: "0.4rem" }} /> : null}
              Send test now
            </Button>
          </div>
          <p style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", opacity: 0.55, marginTop: "0.75rem" }}>
            A test send doesn't consume this month's scheduled reminder.
          </p>
        </>
      )}
    </div>
  );
}

function OverchargeThresholdCard() {
  const [value, setValue] = useState("");
  const [original, setOriginal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet("/settings").then(d => {
      const t = Number(d?.overcharge_threshold_pct ?? 0);
      setOriginal(t);
      setValue(t.toFixed(2));
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const n = parseFloat(value);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Enter a non-negative percentage (e.g. 2.5)");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, null, {
        params: { overcharge_threshold_pct: Number(n.toFixed(2)) },
      });
      const saved = Number(res.data?.overcharge_threshold_pct ?? n);
      setOriginal(saved);
      setValue(saved.toFixed(2));
      toast.success(saved === 0 ? "Threshold cleared — every positive difference counts as an overcharge." : `Threshold set to ${saved.toFixed(2)}% variance. Dashboard will recompute on next load.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setValue("0.00");
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, null, { params: { overcharge_threshold_pct: 0 } });
      setOriginal(0);
      toast.success("Threshold cleared.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear");
    } finally {
      setSaving(false);
    }
  };

  const dirty = Number.parseFloat(value || "0") !== original;

  return (
    <div className="card" style={{ marginBottom: "2rem" }} data-testid="overcharge-threshold-card">
      <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
        Overcharge Threshold
      </h2>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.85, maxWidth: "640px" }}>
        Set the minimum price variance (%) that counts as an overcharge — i.e. how far above the
        price-file price an invoiced line must be. Anything below this is treated as noise and excluded
        everywhere (dashboard totals, supplier metrics, top overcharges, trends, reports CSV). Being a
        percentage, it works across currencies. Use <strong>0</strong> to count every positive difference (default).
      </p>

      {loading ? (
        <p style={{ opacity: 0.6 }}>Loading…</p>
      ) : (
        <div style={{ display: "flex", alignItems: "flex-end", gap: "0.75rem", flexWrap: "wrap" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="overcharge-threshold-input">
              Variance cutoff (%)
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <input
                id="overcharge-threshold-input"
                type="number"
                step="0.1"
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="form-input"
                data-testid="overcharge-threshold-input"
                style={{ width: "140px", fontFamily: "monospace", fontSize: "1rem" }}
              />
              <span style={{ color: "var(--octopus-voltage)", fontFamily: "monospace", fontSize: "1rem" }}>%</span>
            </div>
            <p style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", marginTop: "0.3rem", opacity: 0.6 }}>
              Currently saved: <strong style={{ color: "var(--octopus-ice)" }}>{original.toFixed(2)}%</strong>
              {original > 0 && <> · variances &lt; {original.toFixed(2)}% above the price-file price are ignored</>}
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="btn btn-primary"
            data-testid="save-overcharge-threshold-btn"
          >
            {saving ? "Saving…" : dirty ? "Save threshold" : "Saved"}
          </Button>
          {original > 0 && (
            <Button
              onClick={handleClear}
              disabled={saving}
              className="btn btn-secondary"
              data-testid="clear-overcharge-threshold-btn"
            >
              Clear
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
